import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import { AllMarketingCalendar, WeeklyReports, SeoReports, PlanningView, TeamAdmin, SettingsView } from "./HubViews";
import { createClient } from "@supabase/supabase-js";

// ---------- Design tokens ----------
// ink: #14171a (sidebar/dark)  canvas: #eef1ee (main bg)  panel: #ffffff
// teal: #3ea4b1 (brand)  gold: #d4a017  indigo: #5b5f97  orange: #e2572b  charcoal: #3a3d40

const SWATCH = {
  teal: { bg: "#e4f3f4", fg: "#1f6e79", dot: "#3ea4b1" },
  indigo: { bg: "#eceaf5", fg: "#42456e", dot: "#5b5f97" },
  gold: { bg: "#fdedc2", fg: "#8a6b0a", dot: "#d4a017" },
  orange: { bg: "#fbe6de", fg: "#a53f1f", dot: "#e2572b" },
  charcoal: { bg: "#e9e9ea", fg: "#3a3d40", dot: "#3a3d40" },
};

function ChannelIcon({ name }) {
  const common = { width: 20, height: 20, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8 };
  if (name === "home") return <svg {...common}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>;
  if (name === "mail") return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></svg>;
  if (name === "instagram") return <svg {...common}><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg>;
  if (name === "facebook") return <svg {...common}><path d="M15 4h-2a4 4 0 0 0-4 4v2H7v4h2v6h4v-6h3l1-4h-4V8a1 1 0 0 1 1-1h3z"/></svg>;
  if (name === "link") return <svg {...common}><path d="M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1.5 1.5"/><path d="M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1.5-1.5"/></svg>;
  return <svg {...common}><path d="M16 8v8a4 4 0 1 1-4-4"/><path d="M16 8a4 4 0 0 0 4 4V8a4 4 0 0 1-4-4z"/></svg>;
}

// ---------- Per-channel content banks ----------
// Each idea: title, tag (platform/format/type), type (short descriptor), steps (2 prep steps), body (caption/copy)

const TIKTOK_IDEAS = [
  { title: "POV: Your print shop has unlimited budget.", tag: "Veo 3", type: "AI Hook + Real Footage", steps: ["Film real shop b-roll: printing, embroidery, customer reactions.", "Generate a 3-second AI hook in Veo 3, then edit in CapCut Pro."], body: "You won't believe what happens next 👀 #JGGraphics #ScreenPrinting #CustomApparel #Sacramento" },
  { title: "Giant 30-foot T-shirt rolling through town.", tag: "Veo 3", type: "Cinematic AI Hook", steps: ["Generate the giant-shirt hook in Veo 3.", "Cut with real shop footage in CapCut Pro."], body: "We may have gone a little big on this one 👕 #JGGraphics #ScreenPrinting #CustomApparel" },
  { title: "Embroidery machine stitching by itself overnight.", tag: "Kling AI", type: "Storytelling Reveal", steps: ["Film a real embroidery run for the reveal shot.", "Generate the eerie overnight hook in Kling AI."], body: "The machines never stop 🧵 #JGGraphics #Embroidery #CustomApparel" },
  { title: "Shirt cannon launches shirts across Sacramento.", tag: "Veo 3", type: "Viral Stunt Hook", steps: ["Generate the shirt-cannon hook in Veo 3.", "Splice in real shop footage, edit in CapCut Pro."], body: "Sacramento, heads up 🚀 #JGGraphics #ScreenPrinting #Sacramento" },
  { title: "Before/after apparel transformations.", tag: "Runway", type: "Real Footage Enhance", steps: ["Film a real blank-to-finished garment shot.", "Enhance transition/background in Runway."], body: "Blank to branded in seconds ✨ #JGGraphics #CustomApparel" },
  { title: "Customer orders 10 shirts... receives 10,000.", tag: "Veo 3", type: "Comedy Hook", steps: ["Generate the warehouse-of-shirts hook in Veo 3.", "Edit with comedic timing in CapCut Pro."], body: "We may have overdone this order 😅 #JGGraphics #ScreenPrinting" },
  { title: "What people think screen printing is vs. what actually happens.", tag: "CapCut Pro", type: "Trend Format", steps: ["Film both the \"expectation\" and \"reality\" shots in shop.", "Edit using the trending split-screen template in CapCut Pro."], body: "Nobody warned us about the ink 😂 #JGGraphics #ScreenPrinting #BehindTheScenes" },
  { title: "Time-lapse from blank shirt to finished print.", tag: "Runway", type: "Real Footage Enhance", steps: ["Film a real time-lapse of a full print run.", "Smooth and color-grade in Runway."], body: "60 seconds, start to finish ⏱️ #JGGraphics #ScreenPrinting" },
  { title: "AI imagines custom apparel in 2050.", tag: "Kling AI", type: "Futuristic Concept", steps: ["Generate futuristic apparel concepts in Kling AI.", "Edit and caption in CapCut Pro."], body: "The future of custom apparel, according to AI 🤖 #JGGraphics #CustomApparel" },
  { title: "The life story of a T-shirt.", tag: "Pika", type: "Meme / Storytelling", steps: ["Generate the shirt's \"life story\" hook in Pika.", "Edit narrative pacing in CapCut Pro."], body: "From cotton field to custom order 🧶 #JGGraphics #ScreenPrinting" },
  { title: "Mascot goose runs the print shop for a day.", tag: "Veo 3", type: "Mascot / Brand Character", steps: ["Generate the goose-mascot hook in Veo 3.", "Cut with real shop b-roll in CapCut Pro."], body: "New management just dropped 🪿 #JGGraphics #ScreenPrinting" },
  { title: "It's raining hats over the shop parking lot.", tag: "Veo 3", type: "Cinematic AI Hook", steps: ["Generate the raining-hats hook in Veo 3.", "Blend with real shop exterior footage in CapCut Pro."], body: "Forecast today: 100% chance of embroidery 🧢 #JGGraphics #CustomApparel" },
  { title: "Giant embroidered hoodie walks through downtown Sacramento.", tag: "Veo 3", type: "Cinematic AI Hook", steps: ["Generate the giant-hoodie hook in Veo 3.", "Splice with real Sacramento street footage in CapCut Pro."], body: "Someone call city hall 😂 #JGGraphics #Sacramento #CustomApparel" },
  { title: "Shirts forged like swords in a blacksmith's shop.", tag: "Kling AI", type: "Fantasy Concept", steps: ["Generate the sword-forging hook in Kling AI.", "Edit with dramatic pacing in CapCut Pro."], body: "Forged, not just printed ⚔️ #JGGraphics #CustomApparel" },
  { title: "Reacting to the wildest custom design request we've ever gotten.", tag: "CapCut Pro", type: "Reaction / Comedy", steps: ["Pull a real (anonymized) customer request as the on-screen text.", "Film a genuine reaction shot in the shop."], body: "We really printed this 😂 #JGGraphics #CustomApparel #BehindTheScenes" },
  { title: "Speed-run: rush order from phone call to delivery.", tag: "Runway", type: "Process", steps: ["Film the full rush-order workflow start to finish.", "Speed-ramp the footage in Runway for a fast-paced cut."], body: "Rush order, real time (sped up) ⏩ #JGGraphics #ScreenPrinting" },
  { title: "Ink colors mixing in close-up, oddly satisfying.", tag: "Runway", type: "Satisfying Content", steps: ["Film a macro shot of ink colors blending on the mixing table.", "Smooth the motion and color in Runway."], body: "Watch till the swirl 🌀 #JGGraphics #ScreenPrinting #OddlySatisfying" },
  { title: "Heat press slam-down in extreme slow motion.", tag: "Kling AI", type: "Satisfying / Cinematic", steps: ["Film a real heat press cycle in slow motion.", "Enhance the slow-mo drama in Kling AI."], body: "The most satisfying sound in the shop 🔥 #JGGraphics #CustomApparel" },
  { title: "Guess the print method: screen print, DTF, or embroidery?", tag: "CapCut Pro", type: "Trend / Quiz Format", steps: ["Film three close-up shots, one per method.", "Edit as a trending guess-the-answer quiz format."], body: "Bet you can't get all three right 👀 #JGGraphics #ScreenPrinting #Embroidery" },
  { title: "Ranking the weirdest orders we've ever printed.", tag: "CapCut Pro", type: "Comedy / Ranking", steps: ["Pull 3-5 real (anonymized) unusual past orders.", "Edit as a countdown-style ranking video."], body: "Number one is still undefeated 😅 #JGGraphics #CustomApparel" },
  { title: "Jersey reveal for a local youth sports team.", tag: "Veo 3", type: "Reveal / Customer Feature", steps: ["Get permission to film the team's jersey unveiling.", "Generate a hype-reveal AI hook in Veo 3 to open the video."], body: "New season, new look 🏆 #JGGraphics #TeamGear #Sacramento" },
  { title: "Watch an AI design a custom shirt logo in real time.", tag: "Kling AI", type: "Tech / AI Hook", steps: ["Generate an on-screen AI logo-design sequence in Kling AI.", "Cut to the real finished printed shirt in CapCut Pro."], body: "From prompt to print 🤖 #JGGraphics #CustomApparel" },
  { title: "What's in my print shop tool bag.", tag: "CapCut Pro", type: "Trend Format", steps: ["Lay out real shop tools and supplies for the shot.", "Film using the trending \"what's in my bag\" format."], body: "Everything we need to get the job done 🧰 #JGGraphics #ScreenPrinting" },
  { title: "Guess the machine by its sound only.", tag: "CapCut Pro", type: "Trend / Audio Format", steps: ["Record clean audio clips from 3-4 different shop machines.", "Edit as a sound-off guessing game format."], body: "Shop veterans only 🔊 #JGGraphics #BehindTheScenes" },
  { title: "ASMR folding and packing a huge bulk order.", tag: "Runway", type: "Satisfying / ASMR", steps: ["Film close-up folding and boxing footage with clean audio.", "Enhance the crisp ASMR sound and visuals in Runway."], body: "Oddly calming, honestly 📦 #JGGraphics #ASMR #CustomApparel" },
  { title: "A rejected shirt design gets a second-chance redo.", tag: "Kling AI", type: "Storytelling", steps: ["Show the original rejected design mockup.", "Generate a redemption-arc transition to the approved final in Kling AI."], body: "Round two hit different 🔁 #JGGraphics #CustomApparel" },
  { title: "Employees battle: fastest heat press vs. fastest screen print.", tag: "Veo 3", type: "Comedy / Competition", steps: ["Film a real friendly race between two team members.", "Add a countdown/scoreboard AI overlay hook in Veo 3."], body: "Who's really the fastest in the shop? 🏁 #JGGraphics #TeamJG" },
  { title: "Old logo vs. new logo: a client rebrand glow-up.", tag: "Runway", type: "Transformation", steps: ["Get permission to show the client's old vs. new branded apparel.", "Build a smooth before/after transition in Runway."], body: "Rebrands hit different when they're stitched in 🧵 #JGGraphics #CustomApparel" },
  { title: "If screen printing was a video game, this is the level-up screen.", tag: "Veo 3", type: "Futuristic / Meme", steps: ["Generate a retro video-game \"level up\" AI hook in Veo 3.", "Cut to a real finished print as the \"reward\" shot."], body: "New high score: 500 shirts printed today 🎮 #JGGraphics #ScreenPrinting" },
  { title: "Answering comments: can you print on this?", tag: "CapCut Pro", type: "Trend / Q&A", steps: ["Pull 3-4 real comments asking about unusual print surfaces.", "Film quick answer clips for each in the shop."], body: "Yes, we've printed on weirder things 😂 #JGGraphics #CustomApparel" },
  { title: "A day of orders sped up into 30 seconds.", tag: "Runway", type: "Process", steps: ["Film continuous b-roll across a full shop day.", "Speed-ramp and compress the footage in Runway."], body: "One day at JG Graphics, condensed ⏱️ #JGGraphics #BehindTheScenes" },
  { title: "A giant spool of embroidery thread unravels across the shop floor.", tag: "Veo 3", type: "Cinematic AI Hook", steps: ["Generate the unraveling-thread hook in Veo 3.", "Blend with real embroidery machine footage in CapCut Pro."], body: "We may need a bigger shop 🧵 #JGGraphics #Embroidery" },
  { title: "Surprise freebie added to a customer's order.", tag: "Kling AI", type: "Feel-good / Reveal", steps: ["Film a genuine customer pickup or delivery moment.", "Generate a warm reveal-style AI hook in Kling AI to open it."], body: "Just because 🎁 #JGGraphics #CustomerAppreciation" },
  { title: "Screen printing bloopers and fails compilation.", tag: "CapCut Pro", type: "Comedy", steps: ["Pull clips of harmless real shop mishaps (misprints, smudges, etc).", "Edit as a fast-paced blooper reel with comedic sound effects."], body: "Not our finest moments 😂 #JGGraphics #BehindTheScenes" },
  { title: "Unboxing a massive roll of blank shirts like a mystery box.", tag: "Pika", type: "Unboxing / Trend", steps: ["Film a real bulk blank-apparel delivery being opened.", "Edit using the trending mystery-unboxing format in Pika."], body: "Blank canvas, endless potential 📦 #JGGraphics #CustomApparel" },
  { title: "Cost breakdown: what actually goes into pricing a custom shirt.", tag: "CapCut Pro", type: "Educational", steps: ["Pull real (rounded) cost categories: blank, ink, labor, setup.", "Build a simple on-screen breakdown graphic in CapCut Pro."], body: "Ever wonder what you're really paying for? 🧾 #JGGraphics #ScreenPrinting" },
  { title: "Myth-busting common screen printing misconceptions.", tag: "CapCut Pro", type: "Educational", steps: ["List 3-4 common customer misconceptions about the process.", "Film quick myth vs. fact segments in the shop."], body: "Let's clear this up 🙋 #JGGraphics #ScreenPrinting" },
  { title: "Rate my shirt: customers react to their finished designs.", tag: "CapCut Pro", type: "Trend / Reaction", steps: ["Get permission to film a customer's genuine reaction on pickup.", "Edit with an on-screen rating/reaction overlay."], body: "10/10 reactions only 🙌 #JGGraphics #CustomerLove" },
  { title: "Printing on the weirdest material we've ever tried.", tag: "Kling AI", type: "Novelty / Comedy", steps: ["Film a real unusual print job (or recreate a past one).", "Generate a dramatic \"can it be done\" AI hook in Kling AI."], body: "We said yes to this one 😅 #JGGraphics #CustomApparel" },
  { title: "Night shift at the shop: what happens after hours.", tag: "Runway", type: "Behind the Scenes", steps: ["Film the shop running a late production night.", "Enhance the moody lighting in Runway."], body: "The shop never really sleeps 🌙 #JGGraphics #BehindTheScenes" },
  { title: "Our very first order ever vs. today's setup.", tag: "Runway", type: "Transformation / Nostalgia", steps: ["Pull an old photo or story of the very first order.", "Pair it with current shop footage using a before/after transition in Runway."], body: "From day one to now 🙏 #JGGraphics #Sacramento" },
  { title: "Celebrating our 10,000th shirt printed this year.", tag: "Veo 3", type: "Milestone / Celebration", steps: ["Confirm the real milestone number with the team.", "Generate a celebratory confetti-style AI hook in Veo 3."], body: "10,000 and counting 🎉 #JGGraphics #Milestone" },
  { title: "Every logo we've ever printed, rapid-fire montage.", tag: "CapCut Pro", type: "Montage", steps: ["Pull a wide range of past (permitted) client logo photos.", "Edit as a fast rapid-fire montage with upbeat music."], body: "So many brands, so much fun 🎨 #JGGraphics #CustomApparel" },
  { title: "Quick tip: how to prep your logo file so it doesn't get rejected.", tag: "CapCut Pro", type: "Educational", steps: ["Show a good vs. bad example logo file side by side.", "Film a simple step-by-step tip segment."], body: "Save yourself a delay 💡 #JGGraphics #DesignTips" },
  { title: "Embroidering through a simulated lightning storm.", tag: "Kling AI", type: "Cinematic AI Hook", steps: ["Generate a dramatic storm/lightning AI hook in Kling AI.", "Cut to real embroidery machine footage in CapCut Pro."], body: "Nothing stops production 🌩️ #JGGraphics #Embroidery" },
  { title: "Then vs. now: how much the shop has grown.", tag: "Runway", type: "Transformation", steps: ["Gather old shop photos or footage if available.", "Pair with current shop footage using a growth-timeline edit in Runway."], body: "Small shop energy, big shop results 📈 #JGGraphics #Sacramento" },
  { title: "Size chart confusion: customers vs. reality.", tag: "CapCut Pro", type: "Comedy", steps: ["Write a short relatable skit about sizing mix-ups.", "Film both roles (customer and staff) for the skit."], body: "We've all been here 😂 #JGGraphics #CustomApparel" },
  { title: "AI predicts next year's biggest apparel trend.", tag: "Kling AI", type: "Futuristic Concept", steps: ["Generate a \"trend forecast\" AI concept sequence in Kling AI.", "Add on-screen text predictions in CapCut Pro."], body: "Called it first 🔮 #JGGraphics #CustomApparel" },
  { title: "The sound of a perfect squeegee pull, up close.", tag: "Runway", type: "Satisfying Content", steps: ["Film a macro, high-quality-audio shot of a clean squeegee pull.", "Enhance the crisp sound and visuals in Runway."], body: "That's the sound of a clean print ✅ #JGGraphics #ScreenPrinting" },
  { title: "A customer surprises us with a review on camera.", tag: "Veo 3", type: "Social Proof / Feel-good", steps: ["Get permission to film a genuine customer testimonial moment.", "Add a warm, understated AI intro hook in Veo 3."], body: "This made our whole week 🙏 #JGGraphics #CustomerLove" },
];

const INSTAGRAM_IDEAS = [
  { title: "Behind-the-scenes: a design goes from art file to finished garment.", tag: "Reel", type: "Process", steps: ["Film art approval + press setup.", "Edit as a fast-cut Reel with trending audio."], body: "From screen to shirt 🎨 Swipe to see the whole process. #JGGraphics #ScreenPrinting" },
  { title: "Customer spotlight: a local business wearing their new branded gear.", tag: "Carousel", type: "Customer Feature", steps: ["Get photo permission from the customer.", "Design a branded carousel template."], body: "Big thanks to [customer] for trusting us with their team gear 🙌 #JGGraphics #LocalBusiness" },
  { title: "Before/after: blank shirt to full-color print.", tag: "Reel", type: "Transformation", steps: ["Shoot the blank garment and finished print side by side.", "Cut a quick before/after transition in Reels editor."], body: "Blank canvas, big glow-up ✨ #JGGraphics #CustomApparel" },
  { title: "Meet the team: who's behind JG Graphics.", tag: "Story", type: "Team Feature", steps: ["Grab a quick phone interview clip with a team member.", "Post as a Story sequence with polls/questions stickers."], body: "Say hi to the crew behind your custom gear 👋" },
  { title: "Poll: which logo placement do you like better?", tag: "Story", type: "Engagement", steps: ["Pick two real logo placement mockups.", "Post as a Story with a poll sticker."], body: "Left or right chest? You decide 👀" },
  { title: "Embroidery close-up: oddly satisfying stitching.", tag: "Reel", type: "Satisfying Content", steps: ["Film a slow-motion macro shot of the embroidery machine.", "Add a trending satisfying-content audio track."], body: "Watch till the end 🧵 #JGGraphics #Embroidery #OddlySatisfying" },
  { title: "Customer testimonial quote card.", tag: "Static Post", type: "Social Proof", steps: ["Pull a real quote from a recent review.", "Design a branded quote card."], body: "Nothing beats hearing this from our customers 💬 #JGGraphics" },
  { title: "New product drop: hoodies just added to the shop.", tag: "Carousel", type: "Product Launch", steps: ["Photograph the new hoodie in shop colors.", "Build a multi-slide carousel with pricing/CTA."], body: "New drop alert 🚨 Custom hoodies now available. Link in bio. #JGGraphics" },
  { title: "Fast facts: how many shirts we print in a day.", tag: "Static Post", type: "Brand Stat", steps: ["Pull real production numbers from the shop.", "Design a bold stat-card graphic."], body: "The numbers don't lie 📊 #JGGraphics #ScreenPrinting" },
  { title: "Day in the life of a screen print order.", tag: "Reel", type: "Process", steps: ["Film order intake through final packaging.", "Edit as a day-in-the-life Reel with captions."], body: "From click to closet 📦 #JGGraphics #CustomApparel" },
];

const FACEBOOK_IDEAS = [
  { title: "Share a finished order gallery for a local sports team.", tag: "Photo Post", type: "Customer Feature", steps: ["Get photo permission from the team/coach.", "Compile a short photo album."], body: "Proud to outfit [team name] this season! ⚾️🏀 #JGGraphics" },
  { title: "Announce a limited-time bundle discount.", tag: "Community Update", type: "Promo", steps: ["Confirm discount terms with sales team.", "Write a short, clear announcement post."], body: "This week only: bundle & save on team orders. Message us to get started!" },
  { title: "Post a video walkthrough of the embroidery machine in action.", tag: "Video Post", type: "Process", steps: ["Film a steady 30-45 second clip of the machine running.", "Add captions for sound-off viewing."], body: "Ever wondered how embroidery actually works? Here's a look inside our shop." },
  { title: "Promote an upcoming pop-up or vendor event.", tag: "Event", type: "Event Promo", steps: ["Confirm event date, time, and location.", "Create a Facebook Event with shop details."], body: "Come see us in person! We'll be at [event] on [date] — stop by and say hi." },
  { title: "Ask followers what they want printed next.", tag: "Community Update", type: "Engagement", steps: ["Draft a simple open-ended question.", "Post and monitor comments for response ideas."], body: "What should we print next? Drop your ideas below 👇" },
  { title: "Share a case study: how a local business grew with branded merch.", tag: "Photo Post", type: "Case Study", steps: ["Interview the customer briefly for quotes.", "Write a short case-study style post with photos."], body: "How branded merch helped [business] stand out at every event." },
  { title: "Repost a customer's tagged photo wearing JG gear.", tag: "Photo Post", type: "User-Generated Content", steps: ["Get permission to repost the tagged photo.", "Credit the original poster in the caption."], body: "Love seeing our gear out in the wild 🙌 Thanks for the tag, [customer]!" },
  { title: "Highlight a staff pick product of the week.", tag: "Photo Post", type: "Product Spotlight", steps: ["Ask a team member to pick a favorite product.", "Photograph it in good lighting."], body: "This week's staff pick: [product]. Ask us how to customize yours!" },
];

const EMAIL_IDEAS = [
  { title: "Seasonal promo: % off bulk team orders.", tag: "Promo", type: "Discount Campaign", steps: ["Confirm discount %, terms, and expiration with sales.", "Draft subject line + build in email platform."], body: "Subject: Save on team orders this month 🏆 — Preview: Bulk pricing, faster turnaround, and a limited-time discount for teams and businesses." },
  { title: "Monthly newsletter: recent projects + shop news.", tag: "Newsletter", type: "Newsletter", steps: ["Pull 3-4 recent project photos.", "Draft short recap copy for each."], body: "Subject: What we've been up to at JG Graphics — Preview: A look at recent projects, new products, and what's coming next." },
  { title: "New product announcement: hats now available.", tag: "Announcement", type: "Product Launch", steps: ["Confirm pricing and available styles.", "Draft launch copy + product photos."], body: "Subject: New: Custom hats now available 🧢 — Preview: Add embroidered hats to your next order. Here's how to get started." },
  { title: "Win-back email to customers who haven't ordered in 6+ months.", tag: "Win-back", type: "Re-engagement", steps: ["Pull the segment of lapsed customers.", "Draft a friendly check-in + incentive offer."], body: "Subject: We miss printing for you 👋 — Preview: It's been a while — here's something to bring you back." },
  { title: "Educational: how to prep your logo file for embroidery.", tag: "Educational", type: "How-To", steps: ["Gather 2-3 example logo files (good vs. bad).", "Write simple step-by-step prep guidance."], body: "Subject: Get embroidery-ready artwork in 3 steps — Preview: Avoid delays with these quick file prep tips." },
  { title: "Customer appreciation: thank-you + referral discount.", tag: "Promo", type: "Loyalty", steps: ["Confirm referral discount terms.", "Draft a short thank-you note + referral CTA."], body: "Subject: Thank you from JG Graphics 🙏 — Preview: Here's a little something for spreading the word." },
];

// ---------- Cadence: how many items land on a given day-of-month ----------
function tiktokCount(day) { return [1, 2, 1, 2, 1, 2, 1][day % 7]; }
function instagramCount(day) { return [1, 1, 2, 1, 1, 2, 1][day % 7]; }
function facebookCount(day) { return [1, 0, 1, 1, 0, 1, 0][day % 7]; }
function emailCount(y, m, day) {
  const dow = new Date(y, m, day).getDay(); // 0 Sun - 6 Sat
  return dow === 2 || dow === 5 ? 1 : 0; // Tuesdays & Fridays
}

// ---------- Channel configuration ----------
const CHANNEL_CONFIG = {
  tiktok: {
    label: "TikTok", icon: "tiktok", itemNoun: "video", tagLabel: "Platform",
    tagSwatch: { "Veo 3": "teal", "Runway": "indigo", "Kling AI": "gold", "Pika": "orange", "CapCut Pro": "charcoal" },
    tagOptions: ["Veo 3", "Runway", "Kling AI", "Pika", "CapCut Pro"],
    stepsLabel: "Shot List", bodyLabel: "Suggested Caption",
    createdLabel: "Created/Uploaded", doneLabel: "Posted",
    linkLabel: "Finished File Link", linkPlaceholder: "Paste Dropbox / Drive link…",
    ideaBank: TIKTOK_IDEAS, countFor: (y, m, day) => tiktokCount(day),
    aiContext: "short-form videos (Veo 3, Runway, Kling AI, Pika hooks edited in CapCut Pro), typically 1-2 per day",
  },
  instagram: {
    label: "Instagram", icon: "instagram", itemNoun: "post", tagLabel: "Format",
    tagSwatch: { "Reel": "teal", "Carousel": "indigo", "Static Post": "charcoal", "Story": "gold" },
    tagOptions: ["Reel", "Carousel", "Static Post", "Story"],
    stepsLabel: "Prep Steps", bodyLabel: "Suggested Caption",
    createdLabel: "Created/Uploaded", doneLabel: "Posted",
    linkLabel: "Finished File Link", linkPlaceholder: "Paste Dropbox / Drive link…",
    ideaBank: INSTAGRAM_IDEAS, countFor: (y, m, day) => instagramCount(day),
    aiContext: "Instagram Reels, Carousels, Static Posts, and Stories",
  },
  facebook: {
    label: "Facebook", icon: "facebook", itemNoun: "post", tagLabel: "Format",
    tagSwatch: { "Photo Post": "charcoal", "Video Post": "teal", "Event": "orange", "Community Update": "indigo" },
    tagOptions: ["Photo Post", "Video Post", "Event", "Community Update"],
    stepsLabel: "Prep Steps", bodyLabel: "Post Copy",
    createdLabel: "Created/Uploaded", doneLabel: "Posted",
    linkLabel: "Finished File Link", linkPlaceholder: "Paste Dropbox / Drive link…",
    ideaBank: FACEBOOK_IDEAS, countFor: (y, m, day) => facebookCount(day),
    aiContext: "Facebook photo posts, video posts, events, and community updates",
  },
  email: {
    label: "Email Marketing", icon: "mail", itemNoun: "email", tagLabel: "Type",
    tagSwatch: { "Promo": "orange", "Newsletter": "teal", "Announcement": "indigo", "Win-back": "gold", "Educational": "charcoal" },
    tagOptions: ["Promo", "Newsletter", "Announcement", "Win-back", "Educational"],
    stepsLabel: "Prep Steps", bodyLabel: "Subject + Preview Text",
    createdLabel: "Drafted", doneLabel: "Sent",
    linkLabel: "Email Draft Link", linkPlaceholder: "Paste link to draft (Mailchimp, Klaviyo, etc.)…",
    ideaBank: EMAIL_IDEAS, countFor: (y, m, day) => emailCount(y, m, day),
    aiContext: "marketing emails (promos, newsletters, announcements, win-backs, educational sends)",
  },
};
const CHANNEL_ORDER = ["email", "instagram", "facebook", "tiktok"];

// ---------- Storage helpers (namespaced per channel — calendars never cross) ----------
function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function fmtDate(y, m, d) { return new Date(y, m, d).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }); }
function monthName(y, m) { return new Date(y, m, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" }); }
function pad2(n) { return String(n).padStart(2, "0"); }
function dateInputValue(y, m, d) { return `${y}-${pad2(m + 1)}-${pad2(d)}`; }
function fmtStamp(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
// ---------- Week-view helpers ----------
function startOfWeek(y, m, d) {
  const dt = new Date(y, m, d);
  dt.setDate(dt.getDate() - dt.getDay()); // back up to Sunday
  return { y: dt.getFullYear(), m: dt.getMonth(), d: dt.getDate() };
}
function addDaysToAnchor(anchor, delta) {
  const dt = new Date(anchor.y, anchor.m, anchor.d + delta);
  return { y: dt.getFullYear(), m: dt.getMonth(), d: dt.getDate() };
}
function isoDate(y, m, d) { return dateInputValue(y, m, d); }
function fmtWeekRange(anchor) {
  const start = new Date(anchor.y, anchor.m, anchor.d);
  const end = new Date(anchor.y, anchor.m, anchor.d + 6);
  const startStr = start.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const endStr = end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return `${startStr} – ${endStr}`;
}
function weekCellsIncludes(anchor, dateStr) {
  const start = isoDate(anchor.y, anchor.m, anchor.d);
  const endA = addDaysToAnchor(anchor, 6);
  const end = isoDate(endA.y, endA.m, endA.d);
  return dateStr >= start && dateStr <= end;
}

function defaultItemsForDay(channelId, y, m, day) {
  const cfg = CHANNEL_CONFIG[channelId];
  const count = cfg.countFor(y, m, day);
  const items = [];
  for (let i = 0; i < count; i++) {
    const idx = (day - 1 + i * 3) % cfg.ideaBank.length;
    const base = cfg.ideaBank[idx];
    items.push({ title: base.title, tag: base.tag, type: base.type, steps: base.steps, body: base.body, source: "planned", created: false, done: false, link: "" });
  }
  return items;
}
function isRealCurrentMonth(y, m) {
  const now = new Date();
  return y === now.getFullYear() && m === now.getMonth();
}
function blankMonth(y, m) {
  const out = {};
  const total = daysInMonth(y, m);
  for (let d = 1; d <= total; d++) out[d] = [];
  return out;
}
function blankWeek(anchor) {
  const out = {};
  for (let i = 0; i < 7; i++) {
    const a = addDaysToAnchor(anchor, i);
    out[isoDate(a.y, a.m, a.d)] = [];
  }
  return out;
}
function monthRange(y, m) {
  return { start: dateInputValue(y, m, 1), end: dateInputValue(y, m, daysInMonth(y, m)) };
}
function rowToItem(row) {
  return {
    id: row.id, date: row.item_date, title: row.title, tag: row.tag, type: row.type,
    steps: row.steps || [], body: row.body || "", source: row.source,
    created: row.created_flag, done: row.done, link: row.link || "",
    editedBy: row.edited_by || null, editedAt: row.edited_at || null,
    createdCheckBy: row.created_check_by || null, createdCheckAt: row.created_check_at || null,
    postedBy: row.posted_by || null, postedAt: row.posted_at || null,
    linkAddedBy: row.link_added_by || null, linkAddedAt: row.link_added_at || null,
  };
}
function itemToRow(channelId, y, m, day, item) {
  return {
    channel: channelId, item_date: dateInputValue(y, m, day),
    title: item.title, tag: item.tag, type: item.type, steps: item.steps, body: item.body,
    source: item.source, created_flag: item.created, done: item.done, link: item.link,
  };
}
async function fetchMonth(channelId, y, m) {
  const { start, end } = monthRange(y, m);
  const { data, error } = await supabase
    .from("content_items").select("*")
    .eq("channel", channelId).gte("item_date", start).lte("item_date", end)
    .order("item_date");
  const grouped = blankMonth(y, m);
  if (error) { console.error(error); return grouped; }
  for (const row of data) {
    const day = Number(row.item_date.slice(8, 10));
    if (!grouped[day]) grouped[day] = [];
    grouped[day].push(rowToItem(row));
  }
  return grouped;
}
async function seedCurrentMonthIfEmpty(channelId, y, m) {
  const exists = await monthExists(channelId, y, m);
  if (exists) return;
  const total = daysInMonth(y, m);
  const rows = [];
  for (let d = 1; d <= total; d++) {
    for (const item of defaultItemsForDay(channelId, y, m, d)) rows.push(itemToRow(channelId, y, m, d, item));
  }
  if (rows.length) {
    const { error } = await supabase.from("content_items").insert(rows);
    if (error) console.error(error);
  }
}
async function fetchRange(channelId, startStr, endStr) {
  const { data, error } = await supabase
    .from("content_items").select("*")
    .eq("channel", channelId).gte("item_date", startStr).lte("item_date", endStr)
    .order("item_date");
  const grouped = {};
  if (error) { console.error(error); return grouped; }
  for (const row of data) {
    const key = row.item_date;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(rowToItem(row));
  }
  return grouped;
}
async function loadWeekData(channelId, anchor) {
  const startStr = isoDate(anchor.y, anchor.m, anchor.d);
  const endA = addDaysToAnchor(anchor, 6);
  const endStr = isoDate(endA.y, endA.m, endA.d);
  const data = await fetchRange(channelId, startStr, endStr);
  const merged = blankWeek(anchor);
  Object.keys(data).forEach((k) => { merged[k] = data[k]; });
  return merged;
}
// ---------- Repeat: generates bounded future copies, not a live recurring rule ----------
function addInterval(y, m, d, freq) {
  const dt = new Date(y, m, d);
  if (freq === "daily") dt.setDate(dt.getDate() + 1);
  else if (freq === "weekly") dt.setDate(dt.getDate() + 7);
  else if (freq === "monthly") dt.setMonth(dt.getMonth() + 1);
  return { y: dt.getFullYear(), m: dt.getMonth(), d: dt.getDate() };
}
function generateRepeatDates(startDateStr, freq, maxCount = 12, maxDays = 180) {
  const [sy, smRaw, sdRaw] = startDateStr.split("-").map(Number);
  const startMs = new Date(sy, smRaw - 1, sdRaw).getTime();
  let cur = { y: sy, m: smRaw - 1, d: sdRaw };
  const dates = [];
  for (let i = 0; i < maxCount; i++) {
    cur = addInterval(cur.y, cur.m, cur.d, freq);
    const ms = new Date(cur.y, cur.m, cur.d).getTime();
    if ((ms - startMs) / 86400000 > maxDays) break;
    dates.push(isoDate(cur.y, cur.m, cur.d));
  }
  return dates;
}
async function insertRepeatClones(channelId, item, dateStrs) {
  if (!dateStrs.length) return;
  const rows = dateStrs.map((dateStr) => ({
    channel: channelId, item_date: dateStr,
    title: item.title, tag: item.tag, type: item.type, steps: item.steps, body: item.body,
    source: "manual", created_flag: false, done: false, link: "",
  }));
  const { error } = await supabase.from("content_items").insert(rows);
  if (error) console.error(error);
}
async function loadOrCreateMonth(channelId, y, m) {
  if (isRealCurrentMonth(y, m)) await seedCurrentMonthIfEmpty(channelId, y, m);
  return fetchMonth(channelId, y, m);
}
async function monthExists(channelId, y, m) {
  const { start, end } = monthRange(y, m);
  const { count, error } = await supabase
    .from("content_items").select("id", { count: "exact", head: true })
    .eq("channel", channelId).gte("item_date", start).lte("item_date", end);
  if (error) return false;
  return (count || 0) > 0;
}
async function rangeExists(channelId, startStr, endStr) {
  const { count, error } = await supabase
    .from("content_items").select("id", { count: "exact", head: true })
    .eq("channel", channelId).gte("item_date", startStr).lte("item_date", endStr);
  if (error) return false;
  return (count || 0) > 0;
}
async function clearRange(channelId, startStr, endStr) {
  // Never touch manually-added content — only clear default/AI-generated
  // items so a person's own entries survive an AI (re)scheduling pass.
  const { error } = await supabase.from("content_items").delete()
    .eq("channel", channelId).gte("item_date", startStr).lte("item_date", endStr)
    .neq("source", "manual");
  if (error) console.error(error);
}
async function clearMonth(channelId, y, m) {
  const { start, end } = monthRange(y, m);
  // Never touch manually-added content — only clear default/AI-generated
  // items so a person's own entries survive an AI (re)scheduling pass.
  const { error } = await supabase.from("content_items").delete()
    .eq("channel", channelId).gte("item_date", start).lte("item_date", end)
    .neq("source", "manual");
  if (error) console.error(error);
}
async function bulkInsertMonth(channelId, y, m, dayItemsMap) {
  const rows = [];
  for (const [day, items] of Object.entries(dayItemsMap)) {
    for (const item of items) rows.push(itemToRow(channelId, y, m, Number(day), item));
  }
  if (rows.length) {
    const { error } = await supabase.from("content_items").insert(rows);
    if (error) throw error;
  }
}
async function insertItemRow(channelId, y, m, day, item) {
  const { data, error } = await supabase.from("content_items").insert(itemToRow(channelId, y, m, day, item)).select().single();
  if (error) throw error;
  return rowToItem(data);
}
async function updateItemRow(id, patch) {
  const dbPatch = { updated_at: new Date().toISOString() };
  if ("date" in patch) dbPatch.item_date = patch.date;
  if ("title" in patch) dbPatch.title = patch.title;
  if ("tag" in patch) dbPatch.tag = patch.tag;
  if ("type" in patch) dbPatch.type = patch.type;
  if ("steps" in patch) dbPatch.steps = patch.steps;
  if ("body" in patch) dbPatch.body = patch.body;
  if ("created" in patch) dbPatch.created_flag = patch.created;
  if ("done" in patch) dbPatch.done = patch.done;
  if ("link" in patch) dbPatch.link = patch.link;
  if ("editedBy" in patch) dbPatch.edited_by = patch.editedBy;
  if ("editedAt" in patch) dbPatch.edited_at = patch.editedAt;
  if ("createdCheckBy" in patch) dbPatch.created_check_by = patch.createdCheckBy;
  if ("createdCheckAt" in patch) dbPatch.created_check_at = patch.createdCheckAt;
  if ("postedBy" in patch) dbPatch.posted_by = patch.postedBy;
  if ("postedAt" in patch) dbPatch.posted_at = patch.postedAt;
  if ("linkAddedBy" in patch) dbPatch.link_added_by = patch.linkAddedBy;
  if ("linkAddedAt" in patch) dbPatch.link_added_at = patch.linkAddedAt;
  const { error } = await supabase.from("content_items").update(dbPatch).eq("id", id);
  if (error) console.error(error);
}
async function deleteItemRow(id) {
  const { error } = await supabase.from("content_items").delete().eq("id", id);
  if (error) console.error(error);
}

async function runScheduleBatch(channelId, ny, nm, onBatchDone) {
  const channelCfg = CHANNEL_CONFIG[channelId];
  const total = daysInMonth(ny, nm);
  const batchSize = 5;
  const batches = [];
  for (let start = 1; start <= total; start += batchSize) batches.push([start, Math.min(start + batchSize - 1, total)]);

  const monthObj = {};
  let anyFailure = false;

  for (const [start, end] of batches) {
    try {
      const dayList = [];
      for (let d = start; d <= end; d++) dayList.push(`Day ${d} (${fmtDate(ny, nm, d)})`);

      const prompt =
        `You are planning ${channelCfg.label} content for JG Graphics, a custom screen printing, embroidery, and ` +
        `branded merchandise shop in Sacramento/Folsom, CA. Content type: ${channelCfg.aiContext}. ` +
        `Search for current trends relevant to this content type for small apparel/manufacturing businesses. ` +
        `Then plan content for these days: ${dayList.join("; ")}. For each day, decide how many ${channelCfg.itemNoun}s ` +
        `belong on that day (0 is allowed if nothing fits) based on trend strength and typical cadence for this channel. ` +
        `Respond with ONLY raw JSON, no markdown fences, no commentary, in exactly this shape: ` +
        `{"days":[{"day":<number>,"items":[{"title":"string","tag":"one of: ${channelCfg.tagOptions.join(" | ")}",` +
        `"type":"short string","steps":["string","string"],"body":"short string"}]}]}. Keep every field short.`;

      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 1000,
          tools: [{ type: "web_search_20250305", name: "web_search" }],
          messages: [{ role: "user", content: prompt }],
        }),
      });
      const data = await res.json();
      const textBlocks = (data.content || []).filter((b) => b.type === "text").map((b) => b.text).join("\n");
      const clean = textBlocks.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);

      for (const dayEntry of parsed.days || []) {
        const items = (dayEntry.items || []).map((v) => ({
          title: v.title || "AI-generated concept",
          tag: channelCfg.tagOptions.includes(v.tag) ? v.tag : channelCfg.tagOptions[0],
          type: v.type || "Trend-based",
          steps: Array.isArray(v.steps) && v.steps.length ? v.steps : ["Prep content.", "Publish."],
          body: v.body || "",
          source: "ai-scheduled",
          created: false,
          done: false,
          link: "",
        }));
        monthObj[dayEntry.day] = items;
      }
      for (let d = start; d <= end; d++) if (!monthObj[d]) monthObj[d] = defaultItemsForDay(channelId, ny, nm, d);
    } catch (e) {
      console.error(e);
      anyFailure = true;
      for (let d = start; d <= end; d++) monthObj[d] = defaultItemsForDay(channelId, ny, nm, d);
    }
    if (onBatchDone) onBatchDone();
  }

  await clearMonth(channelId, ny, nm);
  await bulkInsertMonth(channelId, ny, nm, monthObj);
  return anyFailure;
}

async function runScheduleWeek(channelId, anchor) {
  const channelCfg = CHANNEL_CONFIG[channelId];
  const weekDates = [];
  for (let i = 0; i < 7; i++) {
    const a = addDaysToAnchor(anchor, i);
    weekDates.push(isoDate(a.y, a.m, a.d));
  }
  const startStr = weekDates[0];
  const endStr = weekDates[6];

  const weekObj = {};
  let anyFailure = false;

  try {
    const dayList = weekDates.map((ds) => {
      const [y, m, d] = ds.split("-").map(Number);
      return `${fmtDate(y, m - 1, d)} (${ds})`;
    });

    const prompt =
      `You are planning ${channelCfg.label} content for JG Graphics, a custom screen printing, embroidery, and ` +
      `branded merchandise shop in Sacramento/Folsom, CA. Content type: ${channelCfg.aiContext}. ` +
      `Search for current trends relevant to this content type for small apparel/manufacturing businesses. ` +
      `Then plan content for these 7 days: ${dayList.join("; ")}. For each day, decide how many ${channelCfg.itemNoun}s ` +
      `belong on that day (0 is allowed if nothing fits) based on trend strength and typical cadence for this channel. ` +
      `Respond with ONLY raw JSON, no markdown fences, no commentary, in exactly this shape: ` +
      `{"days":[{"date":"YYYY-MM-DD","items":[{"title":"string","tag":"one of: ${channelCfg.tagOptions.join(" | ")}",` +
      `"type":"short string","steps":["string","string"],"body":"short string"}]}]}. Use the exact YYYY-MM-DD dates given above. Keep every field short.`;

    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-6",
        max_tokens: 1000,
        tools: [{ type: "web_search_20250305", name: "web_search" }],
        messages: [{ role: "user", content: prompt }],
      }),
    });
    const data = await res.json();
    const textBlocks = (data.content || []).filter((b) => b.type === "text").map((b) => b.text).join("\n");
    const clean = textBlocks.replace(/```json|```/g, "").trim();
    const parsed = JSON.parse(clean);

    for (const dayEntry of parsed.days || []) {
      if (!weekDates.includes(dayEntry.date)) continue;
      const items = (dayEntry.items || []).map((v) => ({
        title: v.title || "AI-generated concept",
        tag: channelCfg.tagOptions.includes(v.tag) ? v.tag : channelCfg.tagOptions[0],
        type: v.type || "Trend-based",
        steps: Array.isArray(v.steps) && v.steps.length ? v.steps : ["Prep content.", "Publish."],
        body: v.body || "",
        source: "ai-scheduled",
        created: false,
        done: false,
        link: "",
      }));
      weekObj[dayEntry.date] = items;
    }
    for (const ds of weekDates) {
      if (!weekObj[ds]) {
        const [y, m, d] = ds.split("-").map(Number);
        weekObj[ds] = defaultItemsForDay(channelId, y, m - 1, d);
      }
    }
  } catch (e) {
    console.error(e);
    anyFailure = true;
    for (const ds of weekDates) {
      const [y, m, d] = ds.split("-").map(Number);
      weekObj[ds] = defaultItemsForDay(channelId, y, m - 1, d);
    }
  }

  await clearRange(channelId, startStr, endStr);
  const rows = [];
  for (const [dateStr, items] of Object.entries(weekObj)) {
    for (const item of items) {
      rows.push({
        channel: channelId, item_date: dateStr,
        title: item.title, tag: item.tag, type: item.type, steps: item.steps, body: item.body,
        source: item.source, created_flag: item.created, done: item.done, link: item.link,
      });
    }
  }
  if (rows.length) {
    const { error } = await supabase.from("content_items").insert(rows);
    if (error) throw error;
  }
  return anyFailure;
}

function swatchFor(cfg, tag) {
  return SWATCH[cfg.tagSwatch[tag]] || SWATCH.charcoal;
}

function readSaved(key, fallback) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
}
function writeSaved(key, value) {
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* ignore */ }
}

export default function MarketingDashboard() {
  const { assignedChannels, isAdmin, profile, signOut } = useAuth();
  const visibleChannels = CHANNEL_ORDER.filter((id) => assignedChannels.includes(id));
  const today = new Date();
  const [view, setView] = useState(() => readSaved("jg-dash-view", "all"));
  const [settingsNotice] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search).get("settings");
      if (!q) return null;
      return { "slack-connected": "Slack connected. You'll get a welcome message from JG Marketing.", "slack-cancelled": "Slack connection was cancelled.", "slack-error": "Slack couldn't be connected. Try again, or ask Jared to check the Slack app settings." }[q] || null;
    } catch (e) { return null; }
  });
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get("settings")) {
        setView("settings");
        window.history.replaceState({}, "", window.location.pathname);
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [cursor, setCursor] = useState(() => readSaved("jg-dash-cursor", { y: today.getFullYear(), m: today.getMonth() }));
  const [calView, setCalView] = useState(() => readSaved("jg-dash-calview", "month")); // "month" | "week"
  const [weekAnchor, setWeekAnchor] = useState(() => readSaved("jg-dash-week-anchor", startOfWeek(today.getFullYear(), today.getMonth(), today.getDate())));
  const [weekData, setWeekData] = useState(null);
  const [weekDataChannel, setWeekDataChannel] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null); // { item, fromDate }
  const [monthData, setMonthData] = useState(null);
  const [monthDataChannel, setMonthDataChannel] = useState(null);
  const [selectedDay, setSelectedDay] = useState(() => readSaved("jg-dash-selected-day", null));
  const [selectedWeekDate, setSelectedWeekDate] = useState(null);
  const [pendingSelectedDay, setPendingSelectedDay] = useState(null);
  const [showPosts, setShowPosts] = useState(true);
  const [showStories, setShowStories] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [nextScheduled, setNextScheduled] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [scheduleProgress, setScheduleProgress] = useState({ done: 0, total: 0 });
  const [scheduleNote, setScheduleNote] = useState(null);
  const [weekScheduled, setWeekScheduled] = useState(false);
  const [weekScheduling, setWeekScheduling] = useState(false);
  const [weekScheduleNote, setWeekScheduleNote] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newForm, setNewForm] = useState(null);

  const [linksData, setLinksData] = useState(null);
  const [linksError, setLinksError] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkForm, setLinkForm] = useState(null);
  const [editingLinkId, setEditingLinkId] = useState(null);

  useEffect(() => { writeSaved("jg-dash-view", view); }, [view]);
  useEffect(() => { writeSaved("jg-dash-cursor", cursor); }, [cursor]);
  useEffect(() => { writeSaved("jg-dash-selected-day", selectedDay); }, [selectedDay]);
  useEffect(() => { writeSaved("jg-dash-calview", calView); }, [calView]);
  useEffect(() => { writeSaved("jg-dash-week-anchor", weekAnchor); }, [weekAnchor]);

  const isChannel = CHANNEL_CONFIG[view] !== undefined;
  const cfg = isChannel ? CHANNEL_CONFIG[view] : null;
  const canManage = isChannel && view !== "tiktok";
  const nextWeekTarget = addDaysToAnchor(startOfWeek(today.getFullYear(), today.getMonth(), today.getDate()), 7);
  const nextWeekEndAnchor = addDaysToAnchor(nextWeekTarget, 6);
  const hasStoryToggle = isChannel && cfg.tagOptions.includes("Story");
  const storyDotColor = hasStoryToggle ? swatchFor(cfg, "Story").dot : null;
  const postsDotColor = hasStoryToggle
    ? swatchFor(cfg, cfg.tagOptions.find((t) => t !== "Story")).dot
    : null;
  function isVisible(item) {
    const passesToggle = item.tag === "Story" ? showStories : showPosts;
    if (!passesToggle) return false;
    if (!searchTerm.trim()) return true;
    const q = searchTerm.trim().toLowerCase();
    const haystack = [item.title, item.body, item.type, item.tag, ...(item.steps || [])].join(" ").toLowerCase();
    return haystack.includes(q);
  }

  useEffect(() => {
    setShowPosts(true);
    setShowStories(true);
    setSearchTerm("");
  }, [view]);

  useEffect(() => {
    if (!isChannel) return;
    let cancelled = false;
    setMonthData(null);
    setMonthDataChannel(null);
    if (pendingSelectedDay !== null) {
      setSelectedDay(pendingSelectedDay);
      setPendingSelectedDay(null);
    } else {
      setSelectedDay(null);
    }
    setScheduleNote(null);
    const loadingView = view;
    loadOrCreateMonth(view, cursor.y, cursor.m).then((data) => {
      if (cancelled) return;
      setMonthData(data);
      setMonthDataChannel(loadingView);
    });

    const nm = cursor.m === 11 ? 0 : cursor.m + 1;
    const ny = cursor.m === 11 ? cursor.y + 1 : cursor.y;
    monthExists(view, ny, nm).then((exists) => { if (!cancelled) setNextScheduled(exists); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, cursor, isChannel]);

  useEffect(() => {
    if (!isChannel || view === "tiktok") return;
    let cancelled = false;
    setWeekScheduleNote(null);
    const startStr = isoDate(nextWeekTarget.y, nextWeekTarget.m, nextWeekTarget.d);
    const endStr = isoDate(nextWeekEndAnchor.y, nextWeekEndAnchor.m, nextWeekEndAnchor.d);
    rangeExists(view, startStr, endStr).then((exists) => { if (!cancelled) setWeekScheduled(exists); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, isChannel]);

  useEffect(() => {
    if (!isChannel || calView !== "week") return;
    let cancelled = false;
    setWeekData(null);
    setWeekDataChannel(null);
    const loadingView = view;
    loadWeekData(view, weekAnchor).then((merged) => {
      if (cancelled) return;
      setWeekData(merged);
      setWeekDataChannel(loadingView);
    });
    return () => { cancelled = true; };
  }, [view, weekAnchor, calView, isChannel]);


  useEffect(() => {
    if (view !== "links") return;
    let cancelled = false;
    setLinksError(null);
    (async () => {
      const { data, error } = await supabase.from("links_info").select("*").order("updated_at", { ascending: false });
      if (cancelled) return;
      if (error) { setLinksError(error.message); setLinksData([]); }
      else setLinksData(data.map((r) => ({ id: r.id, title: r.title, url: r.url, notes: r.notes })));
    })();
    return () => { cancelled = true; };
  }, [view]);

  function openNewLinkModal() {
    setEditingLinkId(null);
    setLinkForm({ title: "", url: "", notes: "" });
    setShowLinkModal(true);
  }
  function openEditLinkModal(link) {
    setEditingLinkId(link.id);
    setLinkForm({ title: link.title, url: link.url, notes: link.notes });
    setShowLinkModal(true);
  }
  async function submitLinkForm() {
    setLinksError(null);
    if (editingLinkId) {
      const { error } = await supabase
        .from("links_info")
        .update({ title: linkForm.title, url: linkForm.url, notes: linkForm.notes, updated_at: new Date().toISOString() })
        .eq("id", editingLinkId);
      if (error) { setLinksError(`Couldn't save: ${error.message}`); return; }
      setLinksData((prev) => prev.map((l) => (l.id === editingLinkId ? { ...l, ...linkForm } : l)));
    } else {
      const { data, error } = await supabase
        .from("links_info")
        .insert({ title: linkForm.title, url: linkForm.url, notes: linkForm.notes })
        .select().single();
      if (error) { setLinksError(`Couldn't save: ${error.message}`); return; }
      setLinksData((prev) => [{ id: data.id, title: data.title, url: data.url, notes: data.notes }, ...prev]);
    }
    setShowLinkModal(false);
    setLinkForm(null);
    setEditingLinkId(null);
  }
  async function deleteLink(id) {
    setLinksError(null);
    const { error } = await supabase.from("links_info").delete().eq("id", id);
    if (error) { setLinksError(`Couldn't delete: ${error.message}`); return; }
    setLinksData((prev) => prev.filter((l) => l.id !== id));
  }

  useEffect(() => {
    if (view === "home" || CHANNEL_CONFIG[view]) setView(profile && !isAdmin && profile.tabs && profile.tabs.length && !profile.tabs.includes("all") ? ["planning", "reports", "seo", "links"].find((t) => profile.tabs.includes(t)) : "all");
    else if (view === "team" && profile && !isAdmin) setView(profile.tabs && profile.tabs.length ? profile.tabs[0] : "all");
    else if (view !== "settings" && profile && !isAdmin && profile.tabs && profile.tabs.length && !profile.tabs.includes(view)) setView(["all", "planning", "reports", "seo", "links"].find((t) => profile.tabs.includes(t)));
  }, [view, visibleChannels, profile, isAdmin]);

  function changeMonth(delta) {
    setSelectedDay(null);
    setCursor((c) => {
      let m = c.m + delta, y = c.y;
      if (m < 0) { m = 11; y -= 1; }
      if (m > 11) { m = 0; y += 1; }
      return { y, m };
    });
  }

  function changeWeek(delta) {
    setSelectedWeekDate(null);
    setWeekAnchor((a) => addDaysToAnchor(a, delta * 7));
  }

  function goToDay(delta) {
    if (calView === "week") {
      if (!selectedWeekDate) return;
      const [y, m, d] = selectedWeekDate.split("-").map(Number);
      const nextDate = new Date(y, m - 1, d + delta);
      const nextStr = isoDate(nextDate.getFullYear(), nextDate.getMonth(), nextDate.getDate());
      if (!weekCellsIncludes(weekAnchor, nextStr)) {
        setWeekAnchor(startOfWeek(nextDate.getFullYear(), nextDate.getMonth(), nextDate.getDate()));
      }
      setSelectedWeekDate(nextStr);
      return;
    }
    if (selectedDay === null) return;
    const total = daysInMonth(cursor.y, cursor.m);
    const newDay = selectedDay + delta;
    if (newDay < 1) {
      const pm = cursor.m === 0 ? 11 : cursor.m - 1;
      const py = cursor.m === 0 ? cursor.y - 1 : cursor.y;
      setPendingSelectedDay(daysInMonth(py, pm));
      setCursor({ y: py, m: pm });
    } else if (newDay > total) {
      const nm = cursor.m === 11 ? 0 : cursor.m + 1;
      const ny = cursor.m === 11 ? cursor.y + 1 : cursor.y;
      setPendingSelectedDay(1);
      setCursor({ y: ny, m: nm });
    } else {
      setSelectedDay(newDay);
    }
  }

  useEffect(() => {
    if (selectedDay === null && !selectedWeekDate) return;
    function onKeyDown(e) {
      if (e.key === "ArrowLeft") goToDay(-1);
      if (e.key === "ArrowRight") goToDay(1);
      if (e.key === "Escape") { setSelectedDay(null); setSelectedWeekDate(null); }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay, selectedWeekDate, cursor, weekAnchor, calView]);

  const isToday = (d) => cursor.y === today.getFullYear() && cursor.m === today.getMonth() && d === today.getDate();

  function updateItem(key, id, patch) {
    if (calView === "week") {
      setWeekData((prev) => ({ ...prev, [key]: (prev[key] || []).map((v) => (v.id === id ? { ...v, ...patch } : v)) }));
    } else {
      setMonthData((prev) => ({ ...prev, [key]: (prev[key] || []).map((v) => (v.id === id ? { ...v, ...patch } : v)) }));
    }
    updateItemRow(id, patch);
  }

  function deleteItem(key, id) {
    if (calView === "week") {
      setWeekData((prev) => ({ ...prev, [key]: (prev[key] || []).filter((v) => v.id !== id) }));
    } else {
      setMonthData((prev) => ({ ...prev, [key]: (prev[key] || []).filter((v) => v.id !== id) }));
    }
    deleteItemRow(id);
    if (editingId === id) { setEditingId(null); setEditDraft(null); }
  }

  // Moves an item to a new date — used by both the date field in Edit, and drag-and-drop.
  async function moveItemToDate(fromKey, item, toDateStr, extraPatch = {}) {
    if (toDateStr === item.date && Object.keys(extraPatch).length === 0) return;
    const patch = { date: toDateStr, ...extraPatch };
    const updatedItem = { ...item, ...patch };

    if (calView === "week") {
      setWeekData((prev) => {
        const next = { ...prev };
        next[fromKey] = (next[fromKey] || []).filter((v) => v.id !== item.id);
        if (toDateStr in next) next[toDateStr] = [...(next[toDateStr] || []), updatedItem];
        return next;
      });
    } else {
      const [ty, tmRaw, tdRaw] = toDateStr.split("-").map(Number);
      setMonthData((prev) => {
        const next = { ...prev };
        next[fromKey] = (next[fromKey] || []).filter((v) => v.id !== item.id);
        if (ty === cursor.y && tmRaw - 1 === cursor.m) next[tdRaw] = [...(next[tdRaw] || []), updatedItem];
        return next;
      });
    }
    await updateItemRow(item.id, patch);
  }

  function startEdit(id, item) {
    setEditingId(id);
    setEditDraft({ date: item.date, title: item.title, tag: item.tag, type: item.type, stepsText: item.steps.join("\n"), body: item.body, repeat: false, repeatFreq: "weekly" });
  }
  function cancelEdit() { setEditingId(null); setEditDraft(null); }
  async function saveEdit(key, id, currentItem) {
    const steps = editDraft.stepsText.split("\n").map((s) => s.trim()).filter(Boolean);
    const patch = {
      title: editDraft.title, tag: editDraft.tag, type: editDraft.type,
      steps: steps.length ? steps : ["—"], body: editDraft.body,
      editedBy: profile?.full_name || "Someone", editedAt: new Date().toISOString(),
    };
    let finalDate = currentItem.date;
    if (editDraft.date && editDraft.date !== currentItem.date) {
      await moveItemToDate(key, currentItem, editDraft.date, patch);
      finalDate = editDraft.date;
    } else {
      updateItem(key, id, patch);
    }

    if (editDraft.repeat) {
      const repeatDates = generateRepeatDates(finalDate, editDraft.repeatFreq);
      await insertRepeatClones(view, { ...currentItem, ...patch }, repeatDates);
      await refreshVisibleAfterRepeat(repeatDates);
    }

    setEditingId(null);
    setEditDraft(null);
  }

  function handleDropOnDate(toDateStr) {
    if (!draggedItem) return;
    const { item, fromKey } = draggedItem;
    setDraggedItem(null);
    if (toDateStr === item.date) return;
    moveItemToDate(fromKey, item, toDateStr, {
      editedBy: profile?.full_name || "Someone", editedAt: new Date().toISOString(),
    });
  }

  function openNewModal(presetDay) {
    let d, targetY, targetM;
    if (calView === "week") {
      const base = selectedWeekDate ? selectedWeekDate.split("-").map(Number) : [weekAnchor.y, weekAnchor.m + 1, weekAnchor.d];
      targetY = base[0]; targetM = base[1] - 1; d = base[2];
    } else {
      targetY = cursor.y; targetM = cursor.m;
      d = presetDay || (cursor.y === today.getFullYear() && cursor.m === today.getMonth() ? today.getDate() : 1);
    }
    setNewForm({
      date: dateInputValue(targetY, targetM, d),
      title: "", tag: cfg.tagOptions[0], type: "", stepsText: "", body: "",
      repeat: false, repeatFreq: "weekly",
    });
    setShowNewModal(true);
  }

  async function refreshVisibleAfterRepeat(repeatDates) {
    if (calView === "month") {
      const inCurrentMonth = repeatDates.some((ds) => {
        const [ry, rm] = ds.split("-").map(Number);
        return ry === cursor.y && rm - 1 === cursor.m;
      });
      if (inCurrentMonth) {
        const fresh = await fetchMonth(view, cursor.y, cursor.m);
        setMonthData(fresh);
        setMonthDataChannel(view);
      }
    } else {
      const inCurrentWeek = repeatDates.some((ds) => weekCellsIncludes(weekAnchor, ds));
      if (inCurrentWeek) {
        const fresh = await loadWeekData(view, weekAnchor);
        setWeekData(fresh);
        setWeekDataChannel(view);
      }
    }
  }

  async function submitNewItem() {
    const [ny, nmRaw, ndRaw] = newForm.date.split("-").map(Number);
    const nMonthIdx = nmRaw - 1;
    const nDay = ndRaw;
    const nDateStr = newForm.date;
    const steps = newForm.stepsText.split("\n").map((s) => s.trim()).filter(Boolean);
    const itemDraft = {
      title: newForm.title || "Untitled",
      tag: newForm.tag,
      type: newForm.type || "Manual entry",
      steps: steps.length ? steps : ["—"],
      body: newForm.body,
      source: "manual",
      created: false,
      done: false,
      link: "",
    };

    const savedItem = await insertItemRow(view, ny, nMonthIdx, nDay, itemDraft);

    if (ny === cursor.y && nMonthIdx === cursor.m) {
      setMonthData((prev) => {
        const existing = (prev && prev[nDay]) || [];
        return { ...prev, [nDay]: [...existing, savedItem] };
      });
    }
    if (calView === "week" && weekCellsIncludes(weekAnchor, nDateStr)) {
      setWeekData((prev) => {
        const existing = (prev && prev[nDateStr]) || [];
        return { ...prev, [nDateStr]: [...existing, savedItem] };
      });
    }

    if (newForm.repeat) {
      const repeatDates = generateRepeatDates(nDateStr, newForm.repeatFreq);
      await insertRepeatClones(view, itemDraft, repeatDates);
      await refreshVisibleAfterRepeat(repeatDates);
    }

    setShowNewModal(false);
    setNewForm(null);
  }

  async function scheduleNextMonth() {
    const channelId = view;
    const nm = cursor.m === 11 ? 0 : cursor.m + 1;
    const ny = cursor.m === 11 ? cursor.y + 1 : cursor.y;
    const total = daysInMonth(ny, nm);
    const batches = Math.ceil(total / 5);

    setScheduling(true);
    setScheduleNote(null);
    setScheduleProgress({ done: 0, total: batches });

    const anyFailure = await runScheduleBatch(channelId, ny, nm, () => {
      setScheduleProgress((p) => ({ ...p, done: p.done + 1 }));
    });

    setNextScheduled(true);
    setScheduling(false);
    setScheduleNote(
      anyFailure
        ? `Scheduled ${monthName(ny, nm)} — a few days fell back to default ideas after a search hiccup.`
        : `Scheduled all of ${monthName(ny, nm)} from current trends.`
    );
  }

  async function scheduleNextWeekForChannel() {
    const channelId = view;
    setWeekScheduling(true);
    setWeekScheduleNote(null);

    const anyFailure = await runScheduleWeek(channelId, nextWeekTarget);

    setWeekScheduled(true);
    setWeekScheduling(false);
    setWeekScheduleNote(
      anyFailure
        ? `Scheduled ${fmtWeekRange(nextWeekTarget)} — a few days fell back to default ideas after a search hiccup.`
        : `Scheduled ${fmtWeekRange(nextWeekTarget)} from current trends.`
    );

    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const a = addDaysToAnchor(nextWeekTarget, i);
      weekDates.push(isoDate(a.y, a.m, a.d));
    }
    await refreshVisibleAfterRepeat(weekDates);
  }

  const total = monthData ? daysInMonth(cursor.y, cursor.m) : 0;
  const firstWeekday = new Date(cursor.y, cursor.m, 1).getDay();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(d);

  const weekCells = [];
  for (let i = 0; i < 7; i++) {
    const a = addDaysToAnchor(weekAnchor, i);
    weekCells.push({ y: a.y, m: a.m, d: a.d, iso: isoDate(a.y, a.m, a.d) });
  }

  const nm = cursor.m === 11 ? 0 : cursor.m + 1;
  const ny = cursor.m === 11 ? cursor.y + 1 : cursor.y;
  const safeMonthData = monthDataChannel === view ? monthData : null;
  const safeWeekData = weekDataChannel === view ? weekData : null;
  const selectedItemsRaw = calView === "week"
    ? (selectedWeekDate && safeWeekData ? safeWeekData[selectedWeekDate] : null)
    : (selectedDay && safeMonthData ? safeMonthData[selectedDay] : null);
  const selectedItems = selectedItemsRaw ? selectedItemsRaw.filter(isVisible) : null;
  const selectedKey = calView === "week" ? selectedWeekDate : selectedDay;
  const selectedDateLabel = calView === "week" && selectedWeekDate
    ? (() => { const [yy, mmS, ddS] = selectedWeekDate.split("-").map(Number); return fmtDate(yy, mmS - 1, ddS); })()
    : selectedDay ? fmtDate(cursor.y, cursor.m, selectedDay) : "";

  return (
    <div style={{ fontFamily: "Inter, sans-serif", minHeight: "100vh", width: "100%", display: "flex", flexDirection: "column", background: "#eef1ee", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@500;600;700&family=IBM+Plex+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');
        * { box-sizing: border-box; }
        .jg-display { font-family: 'Oswald', sans-serif; letter-spacing: 0.02em; }
        .jg-mono { font-family: 'IBM Plex Mono', monospace; }
        .day-cell { border: 1.5px dashed #c7cdc9; transition: border-color .15s, transform .1s; }
        .day-cell:hover { border-color: #3ea4b1; transform: translateY(-1px); }
        .day-cell.today { border-style: solid; border-color: #3ea4b1; border-width: 2px; }
        button { cursor: pointer; }
        input[type="text"]:focus { outline: 2px solid #3ea4b1; outline-offset: 1px; }
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-thumb { background: #c7cdc9; border-radius: 4px; }
        .jg-top { background: #14171a; position: sticky; top: 0; z-index: 40; padding-top: env(safe-area-inset-top, 0px); }
        .jg-top-inner { max-width: 1280px; margin: 0 auto; display: flex; align-items: center; gap: 18px; padding: 10px 36px; }
        .jg-tabs { display: flex; gap: 4px; flex: 1; min-width: 0; overflow-x: auto; scrollbar-width: none; }
        .jg-tabs::-webkit-scrollbar { display: none; }
        .jg-tab { background: transparent; border: none; color: #a3aaae; font-family: 'Oswald', sans-serif; font-size: 15px; letter-spacing: 0.06em; text-transform: uppercase; padding: 9px 14px; border-radius: 8px; white-space: nowrap; }
        .jg-tab:hover { color: #fff; }
        .jg-tab.active { color: #fff; background: rgba(62,164,177,0.22); box-shadow: inset 0 -2px 0 #3ea4b1; }
        .jg-tab:focus-visible, .jg-signout:focus-visible { outline: 2px solid #3ea4b1; outline-offset: 2px; }
        .jg-user { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
        .jg-user-name { font-size: 12px; }
        .jg-user-btn { display: inline-flex; align-items: center; gap: 6px; background: transparent; border: 1px solid transparent; color: #c7cdc9; border-radius: 7px; padding: 6px 8px; font-family: 'IBM Plex Mono', monospace; }
        .jg-user-btn:hover, .jg-user-btn.active { color: #fff; border-color: #3a3f43; background: rgba(62,164,177,0.18); }
        .jg-user-btn:focus-visible { outline: 2px solid #3ea4b1; outline-offset: 2px; }
        .jg-signout { background: transparent; border: 1px solid #3a3f43; color: #c7cdc9; border-radius: 7px; padding: 6px 10px; font-size: 12px; white-space: nowrap; }
        .jg-main { padding: 28px 36px 48px; }
        @media (max-width: 760px) {
          .jg-top-inner { padding: 8px 12px; gap: 10px; }
          .jg-user-name { display: none; }
          .jg-tab { font-size: 13.5px; padding: 8px 10px; }
          .jg-main { padding: 18px 14px 40px; }
          .jg-main h1.jg-display { font-size: 24px !important; }
        }
      `}</style>

      {/* Top bar */}
      <header className="jg-top">
        <div className="jg-top-inner">
          <img src="data:image/png;base64,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" alt="JG Graphics" style={{ width: 34, height: 34, flexShrink: 0 }} />
          <nav className="jg-tabs" aria-label="Main">
            {[["all", "Calendar"], ["planning", "Planning"], ["reports", "History"], ["seo", "SEO"], ["links", "Links"], ...(isAdmin ? [["team", "Team"]] : [])].filter(([id]) => isAdmin || !profile?.tabs || profile.tabs.includes(id)).map(([id, label]) => (
              <button key={id} onClick={() => setView(id)} className={"jg-tab" + (view === id ? " active" : "")} aria-current={view === id ? "page" : undefined}>{label}</button>
            ))}
          </nav>
          {profile && (
            <div className="jg-user">
              <button onClick={() => setView("settings")} className={"jg-user-btn" + (view === "settings" ? " active" : "")} title="My settings"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/></svg><span className="jg-user-name">{profile.full_name}</span></button>
              <button onClick={signOut} className="jg-signout">Sign out</button>
            </div>
          )}
        </div>
      </header>

      {/* Main */}
      <div className="jg-main" style={{ flex: 1, minWidth: 0, width: "100%", maxWidth: 1280, margin: "0 auto", overflowX: "hidden" }}>
        {view === "home" && (
          <>
            <div className="jg-mono" style={{ fontSize: 11, color: "#3ea4b1", letterSpacing: "0.08em", marginBottom: 4 }}>MARKETING DASHBOARD</div>
            <h1 className="jg-display" style={{ margin: "0 0 6px", fontSize: 30, color: "#14171a", textTransform: "uppercase" }}>Channels</h1>
            <p style={{ margin: "0 0 24px", fontSize: 13.5, color: "#6b7280" }}>Pick a channel to plan and track its content. Each channel keeps its own separate calendar.</p>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14 }}>
              {visibleChannels.map((id) => {
                const c = CHANNEL_CONFIG[id];
                return (
                  <div
                    key={id}
                    onClick={() => setView(id)}
                    style={{ background: "#fff", border: "1px solid #dde2de", borderRadius: 12, padding: 20, cursor: "pointer", transition: "border-color .12s", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3ea4b1"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#dde2de"; }}
                  >
                    <div style={{ width: 42, height: 42, borderRadius: 9, background: "#eef1ee", color: "#14171a", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                      <ChannelIcon name={c.icon} />
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#14171a", marginBottom: 6 }}>{c.label}</div>
                    <span className="jg-mono" style={{ fontSize: 9.5, padding: "3px 8px", borderRadius: 5, background: "#e4f3f4", color: "#1f6e79" }}>LIVE</span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {isChannel && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}>
              <button onClick={() => setView("home")} style={{ background: "none", border: "none", color: "#6b7280", fontSize: 13, padding: 0 }}>← Channels</button>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22, flexWrap: "wrap", gap: 16 }}>
              <div>
                <div className="jg-mono" style={{ fontSize: 11, color: "#3ea4b1", letterSpacing: "0.08em", marginBottom: 4 }}>MARKETING · {cfg.label.toUpperCase()}</div>
                <h1 className="jg-display" style={{ margin: 0, fontSize: 30, color: "#14171a", textTransform: "uppercase" }}>Content Calendar</h1>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                {canManage && (
                  <button
                    onClick={() => openNewModal(selectedDay)}
                    style={{ background: "#fff", color: "#14171a", border: "1.5px solid #14171a", borderRadius: 8, padding: "12px 16px", fontWeight: 600, fontSize: 13 }}
                  >
                    + New
                  </button>
                )}
                {view === "tiktok" ? (
                  <button
                    onClick={scheduleNextMonth}
                    disabled={scheduling || nextScheduled}
                    style={{
                      background: nextScheduled ? "#2f9e44" : scheduling ? "#9bc9cf" : "#3ea4b1",
                      color: "#fff", border: "none", borderRadius: 8, padding: "12px 18px", fontWeight: 600, fontSize: 13,
                      cursor: nextScheduled || scheduling ? "default" : "pointer",
                    }}
                  >
                    {nextScheduled ? "✓ Scheduled" : scheduling ? `Scheduling ${monthName(ny, nm)}… (${scheduleProgress.done}/${scheduleProgress.total})` : `Schedule ${monthName(ny, nm)}'s ${cfg.label}`}
                  </button>
                ) : (
                  <button
                    onClick={scheduleNextWeekForChannel}
                    disabled={weekScheduling || weekScheduled}
                    style={{
                      background: weekScheduled ? "#2f9e44" : weekScheduling ? "#9bc9cf" : "#3ea4b1",
                      color: "#fff", border: "none", borderRadius: 8, padding: "12px 18px", fontWeight: 600, fontSize: 13,
                      cursor: weekScheduled || weekScheduling ? "default" : "pointer",
                    }}
                  >
                    {weekScheduled ? "✓ Scheduled" : weekScheduling ? `Scheduling ${fmtWeekRange(nextWeekTarget)}…` : `Schedule ${fmtWeekRange(nextWeekTarget)}`}
                  </button>
                )}
              </div>
            </div>

            {scheduleNote && <div style={{ marginBottom: 14, color: "#1f6e79", fontSize: 13 }}>{scheduleNote}</div>}
            {weekScheduleNote && <div style={{ marginBottom: 14, color: "#1f6e79", fontSize: 13 }}>{weekScheduleNote}</div>}

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
              {hasStoryToggle ? (
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => setShowPosts((v) => !v)}
                    style={{
                      background: showPosts ? postsDotColor : "#fff",
                      color: showPosts ? "#fff" : postsDotColor,
                      border: `1.5px solid ${postsDotColor}`, borderRadius: 20, padding: "6px 16px", fontSize: 12.5, fontWeight: 600,
                    }}
                  >
                    Posts {showPosts ? "✓" : ""}
                  </button>
                  <button
                    onClick={() => setShowStories((v) => !v)}
                    style={{
                      background: showStories ? storyDotColor : "#fff",
                      color: showStories ? "#fff" : storyDotColor,
                      border: `1.5px solid ${storyDotColor}`, borderRadius: 20, padding: "6px 16px", fontSize: 12.5, fontWeight: 600,
                    }}
                  >
                    Stories {showStories ? "✓" : ""}
                  </button>
                </div>
              ) : <div />}

              <div style={{ position: "relative", width: 240, maxWidth: "100%" }}>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={`Search ${cfg.itemNoun}s…`}
                  style={{ width: "100%", padding: "8px 32px 8px 12px", border: "1px solid #dde2de", borderRadius: 20, fontSize: 12.5 }}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#8a9195", fontSize: 14, padding: 2, lineHeight: 1 }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  onClick={() => setCalView("month")}
                  style={{ background: calView === "month" ? "#14171a" : "#fff", color: calView === "month" ? "#fff" : "#3a3d40", border: "1px solid #14171a", borderRadius: 6, padding: "6px 14px", fontSize: 12.5, fontWeight: 600 }}
                >
                  Month
                </button>
                <button
                  onClick={() => setCalView("week")}
                  style={{ background: calView === "week" ? "#14171a" : "#fff", color: calView === "week" ? "#fff" : "#3a3d40", border: "1px solid #14171a", borderRadius: 6, padding: "6px 14px", fontSize: 12.5, fontWeight: 600 }}
                >
                  Week
                </button>
              </div>

              {calView === "month" ? (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <button onClick={() => changeMonth(-1)} style={{ background: "#fff", border: "1px solid #dde2de", borderRadius: 6, padding: "6px 12px" }}>←</button>
                  <div className="jg-display" style={{ fontSize: 18, color: "#14171a", textTransform: "uppercase" }}>{monthName(cursor.y, cursor.m)}</div>
                  <button onClick={() => changeMonth(1)} style={{ background: "#fff", border: "1px solid #dde2de", borderRadius: 6, padding: "6px 12px" }}>→</button>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <button onClick={() => changeWeek(-1)} style={{ background: "#fff", border: "1px solid #dde2de", borderRadius: 6, padding: "6px 12px" }}>←</button>
                  <div className="jg-display" style={{ fontSize: 16, color: "#14171a", textTransform: "uppercase" }}>{fmtWeekRange(weekAnchor)}</div>
                  <button onClick={() => changeWeek(1)} style={{ background: "#fff", border: "1px solid #dde2de", borderRadius: 6, padding: "6px 12px" }}>→</button>
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: 18, marginBottom: 10, flexWrap: "wrap" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "#6b7280" }}>
                <span style={{ width: 22, height: 14, borderRadius: 3, background: "#fdedc2", borderLeft: "5px solid #d4a017" }} /> AI-scheduled
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "#6b7280" }}>
                <span style={{ width: 22, height: 14, borderRadius: 3, background: "#e9e9ea", borderLeft: "5px solid #14171a" }} /> Manually added
              </span>
              {canManage && (
                <span style={{ fontSize: 12, color: "#8a9195", fontStyle: "italic" }}>Drag a title to a different day to reschedule it</span>
              )}
            </div>

            {calView === "month" ? (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 6, marginBottom: 4 }}>
                  {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
                    <div key={d} className="jg-mono" style={{ textAlign: "center", fontSize: 10, color: "#8a9195", padding: "4px 0" }}>{d.toUpperCase()}</div>
                  ))}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 6 }}>
                  {cells.map((d, i) => {
                    if (!d) return <div key={i} />;
                    const items = safeMonthData ? (safeMonthData[d] || []).filter(isVisible) : null;
                    const doneCount = items ? items.filter((v) => v.done).length : 0;
                    const cellDateStr = dateInputValue(cursor.y, cursor.m, d);
                    return (
                      <div
                        key={i}
                        className={`day-cell${isToday(d) ? " today" : ""}`}
                        onClick={() => items && items.length > 0 && setSelectedDay(d)}
                        onDragOver={(e) => { if (canManage) e.preventDefault(); }}
                        onDrop={(e) => { if (!canManage) return; e.preventDefault(); handleDropOnDate(cellDateStr); }}
                        style={{ background: "#fff", borderRadius: 8, padding: 8, minHeight: 100, minWidth: 0, cursor: items && items.length ? "pointer" : "default", display: "flex", flexDirection: "column", opacity: items && items.length === 0 ? 0.5 : 1 }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <span className="jg-mono" style={{ fontSize: 12, color: "#14171a", fontWeight: 600 }}>{d}</span>
                          {items && items.length > 0 && <span className="jg-mono" style={{ fontSize: 9, color: "#8a9195" }}>{doneCount}/{items.length}</span>}
                        </div>
                        {items && items.length > 0 && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 5, minWidth: 0 }}>
                            {items.map((v) => (
                              <div
                                key={v.id}
                                draggable={canManage}
                                onDragStart={(e) => { e.stopPropagation(); setDraggedItem({ item: v, fromKey: d }); }}
                                title={`${v.title}${v.source === "manual" ? " (Manual)" : v.source === "ai-scheduled" ? " (AI-scheduled)" : ""}`}
                                style={{
                                  display: "flex", alignItems: "center", gap: 4, minWidth: 0,
                                  cursor: canManage ? "grab" : "default",
                                  fontSize: 10.5, color: "#3a3d40", opacity: v.done ? 0.5 : 1,
                                  textDecoration: v.done ? "line-through" : "none",
                                  padding: "2px 4px 2px 5px", borderRadius: 3,
                                  background: v.source === "ai-scheduled" ? "#fdedc2" : v.source === "manual" ? "#e9e9ea" : "#fff",
                                  border: v.source === "ai-scheduled" ? "1px solid #e8cf8e" : v.source === "manual" ? "1px solid #c7cdc9" : "1px solid #e4e7e4",
                                  borderLeft: v.source === "ai-scheduled" ? "5px solid #d4a017" : v.source === "manual" ? "5px solid #14171a" : "4px solid #dde2de",
                                }}
                              >
                                <span style={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0, background: swatchFor(cfg, v.tag).dot }} />
                                {v.source === "ai-scheduled" && <span className="jg-mono" style={{ fontSize: 8, fontWeight: 700, color: "#8a6b0a", flexShrink: 0 }}>AI</span>}
                                {v.source === "manual" && <span className="jg-mono" style={{ fontSize: 8, fontWeight: 700, color: "#14171a", flexShrink: 0 }}>M</span>}
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: 1 }}>{v.title}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 8 }}>
                {weekCells.map((wc) => {
                  const items = safeWeekData ? (safeWeekData[wc.iso] || []).filter(isVisible) : null;
                  const isTodayCol = wc.y === today.getFullYear() && wc.m === today.getMonth() && wc.d === today.getDate();
                  return (
                    <div
                      key={wc.iso}
                      onDragOver={(e) => { if (canManage) e.preventDefault(); }}
                      onDrop={(e) => { if (!canManage) return; e.preventDefault(); handleDropOnDate(wc.iso); }}
                      style={{
                        background: "#fff", borderRadius: 8, padding: 8, minHeight: 320,
                        border: isTodayCol ? "2px solid #3ea4b1" : "1.5px dashed #c7cdc9",
                      }}
                    >
                      <div className="jg-mono" style={{ fontSize: 10, color: "#8a9195", marginBottom: 8, textAlign: "center" }}>
                        {new Date(wc.y, wc.m, wc.d).toLocaleDateString("en-US", { weekday: "short" }).toUpperCase()}
                        <div style={{ fontSize: 14, color: "#14171a", fontWeight: 600, marginTop: 2 }}>{wc.d}</div>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {items && items.map((v) => (
                          <div
                            key={v.id}
                            draggable={canManage}
                            onDragStart={() => setDraggedItem({ item: v, fromKey: wc.iso })}
                            onClick={() => setSelectedWeekDate(wc.iso)}
                            style={{
                              background: v.source === "ai-scheduled" ? "#fdedc2" : v.source === "manual" ? "#e9e9ea" : "#fff",
                              border: v.source === "ai-scheduled" ? "1px solid #e8cf8e" : v.source === "manual" ? "1px solid #c7cdc9" : "1px solid #e4e7e4",
                              borderLeft: v.source === "ai-scheduled" ? "5px solid #d4a017" : v.source === "manual" ? "5px solid #14171a" : "4px solid #dde2de",
                              borderRadius: 6, padding: "6px 8px", cursor: canManage ? "grab" : "pointer",
                              opacity: v.done ? 0.55 : 1,
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4, marginBottom: 3, minWidth: 0 }}>
                              <span style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0, overflow: "hidden" }}>
                                <span style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: swatchFor(cfg, v.tag).dot }} />
                                <span className="jg-mono" style={{ fontSize: 8.5, color: swatchFor(cfg, v.tag).fg, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.tag}</span>
                              </span>
                              {v.source === "ai-scheduled" && <span className="jg-mono" style={{ fontSize: 7.5, fontWeight: 700, color: "#8a6b0a", flexShrink: 0 }}>AI</span>}
                              {v.source === "manual" && <span className="jg-mono" style={{ fontSize: 7.5, fontWeight: 700, color: "#14171a", flexShrink: 0 }}>MANUAL</span>}
                            </div>
                            <div style={{ fontSize: 11, color: "#14171a", lineHeight: 1.3, textDecoration: v.done ? "line-through" : "none", overflowWrap: "break-word" }}>{v.title}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {profile && !isAdmin && Array.isArray(profile.tabs) && profile.tabs.length === 0 && (
          <div style={{ background: "#fff", border: "1px solid #dde2de", borderRadius: 12, padding: 24, color: "#3a3d40", fontSize: 14 }}>You don't have access to any pages yet. Ask an admin to turn some on for you.</div>
        )}
        {view === "all" && (isAdmin || !profile?.tabs || profile.tabs.includes("all")) && <AllMarketingCalendar assignedChannels={assignedChannels} isAdmin={isAdmin} userName={profile?.full_name} userId={profile?.id} />}
        {view === "planning" && <PlanningView assignedChannels={assignedChannels} isAdmin={isAdmin} userName={profile?.full_name} userId={profile?.id} />}
        {view === "reports" && <WeeklyReports />}
        {view === "settings" && <SettingsView userId={profile?.id} userName={profile?.full_name} initialNotice={settingsNotice} />}
        {view === "team" && isAdmin && <TeamAdmin currentUserId={profile?.id} supabaseUrl={process.env.REACT_APP_SUPABASE_URL} supabaseKey={process.env.REACT_APP_SUPABASE_ANON_KEY} createClientFn={createClient} />}
        {view === "seo" && <SeoReports />}

        {view === "links" && (
          <>
            <div className="jg-mono" style={{ fontSize: 11, color: "#3ea4b1", letterSpacing: "0.08em", marginBottom: 4 }}>REFERENCE</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 14, marginBottom: 22 }}>
              <h1 className="jg-display" style={{ margin: 0, fontSize: 30, color: "#14171a", textTransform: "uppercase" }}>Links & Information</h1>
              {isAdmin && (
                <button
                  onClick={openNewLinkModal}
                  style={{ background: "#fff", color: "#14171a", border: "1.5px solid #14171a", borderRadius: 8, padding: "12px 16px", fontWeight: 600, fontSize: 13 }}
                >
                  + New Link
                </button>
              )}
            </div>

            {linksError && (
              <div style={{ marginBottom: 16, padding: "10px 14px", background: "#fbe6de", color: "#a53f1f", borderRadius: 8, fontSize: 13 }}>
                {linksError}
              </div>
            )}

            {linksData === null ? (
              <p style={{ color: "#6b7280", fontSize: 13.5 }}>Loading…</p>
            ) : linksData.length === 0 ? (
              <p style={{ color: "#6b7280", fontSize: 13.5 }}>No links saved yet. Add tools, dashboards, credentials reminders, or anything else worth keeping handy.</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
                {linksData.map((l) => (
                  <div key={l.id} style={{ background: "#fff", border: "1px solid #dde2de", borderRadius: 12, padding: 18 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#14171a", marginBottom: 6 }}>{l.title}</div>
                    {l.url && (
                      <a href={l.url} target="_blank" rel="noreferrer" style={{ fontSize: 12.5, color: "#3ea4b1", wordBreak: "break-all", display: "inline-block", marginBottom: 8 }}>{l.url}</a>
                    )}
                    {l.notes && <div style={{ fontSize: 12.5, color: "#3a3d40", lineHeight: 1.4, marginBottom: 12, whiteSpace: "pre-wrap" }}>{l.notes}</div>}
                    {isAdmin && (
                      <div style={{ display: "flex", gap: 8, borderTop: "1px solid #eef1ee", paddingTop: 10 }}>
                        <button onClick={() => openEditLinkModal(l)} style={{ background: "#eef1ee", color: "#3a3d40", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12 }}>Edit</button>
                        <button onClick={() => deleteLink(l.id)} style={{ background: "#fbe6de", color: "#a53f1f", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12 }}>Delete</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail drawer */}
      {selectedItems && cfg && (
        <div onClick={() => { setSelectedDay(null); setSelectedWeekDate(null); }} style={{ position: "fixed", inset: 0, background: "rgba(20,23,26,0.4)", display: "flex", justifyContent: "flex-end", zIndex: 10 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 420, maxWidth: "92vw", background: "#fff", height: "100%", padding: 28, overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <button
                onClick={() => goToDay(-1)}
                style={{ background: "#eef1ee", border: "none", borderRadius: 6, width: 28, height: 28, fontSize: 14, color: "#3a3d40" }}
                title="Previous day"
              >
                ←
              </button>
              <div className="jg-mono" style={{ fontSize: 11, color: "#3ea4b1" }}>{selectedDateLabel.toUpperCase()}</div>
              <button
                onClick={() => goToDay(1)}
                style={{ background: "#eef1ee", border: "none", borderRadius: 6, width: 28, height: 28, fontSize: 14, color: "#3a3d40" }}
                title="Next day"
              >
                →
              </button>
            </div>
            <h2 className="jg-display" style={{ margin: "0 0 18px", fontSize: 20, color: "#14171a" }}>{selectedItems.length} {cfg.itemNoun}{selectedItems.length !== 1 ? "s" : ""} scheduled</h2>

            {selectedItems.map((v, vi) => (
              <div
                key={v.id}
                style={{
                  border: v.source === "ai-scheduled" ? "1px solid #e8cf8e" : v.source === "manual" ? "1px solid #c7cdc9" : "1px solid #e4e7e4",
                  borderLeft: v.source === "ai-scheduled" ? "5px solid #d4a017" : v.source === "manual" ? "5px solid #14171a" : "4px solid #dde2de",
                  background: v.source === "ai-scheduled" ? "#fffdf7" : v.source === "manual" ? "#fafafa" : "#fff",
                  borderRadius: 10, padding: 16, marginBottom: 14, opacity: v.done && editingId !== v.id ? 0.7 : 1,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                  <div className="jg-mono" style={{ fontSize: 10, color: "#8a9195" }}>{cfg.itemNoun.toUpperCase()} {vi + 1} OF {selectedItems.length}</div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#3a3d40", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={v.created}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          updateItem(selectedKey, v.id, checked
                            ? { created: true, createdCheckBy: profile?.full_name || "Someone", createdCheckAt: new Date().toISOString() }
                            : { created: false, createdCheckBy: null, createdCheckAt: null });
                        }}
                      />
                      {cfg.createdLabel}
                    </label>
                    {v.created && v.createdCheckBy && (
                      <span className="jg-mono" style={{ fontSize: 9, color: "#8a9195" }}>{v.createdCheckBy} · {fmtStamp(v.createdCheckAt)}</span>
                    )}
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#3a3d40", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={v.done}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          updateItem(selectedKey, v.id, checked
                            ? { done: true, postedBy: profile?.full_name || "Someone", postedAt: new Date().toISOString() }
                            : { done: false, postedBy: null, postedAt: null });
                        }}
                      />
                      {cfg.doneLabel}
                    </label>
                    {v.done && v.postedBy && (
                      <span className="jg-mono" style={{ fontSize: 9, color: "#8a9195" }}>{v.postedBy} · {fmtStamp(v.postedAt)}</span>
                    )}
                  </div>
                </div>
                {v.editedBy && (
                  <div className="jg-mono" style={{ fontSize: 9, color: "#8a9195", marginTop: 4 }}>Edited by {v.editedBy} · {fmtStamp(v.editedAt)}</div>
                )}

                {editingId === v.id ? (
                  <div style={{ marginTop: 10 }}>
                    <div className="jg-mono" style={{ fontSize: 9.5, color: "#8a9195", marginBottom: 4 }}>DATE</div>
                    <input type="date" value={editDraft.date} onChange={(e) => setEditDraft({ ...editDraft, date: e.target.value })}
                      style={{ width: "100%", padding: "7px 9px", border: "1px solid #dde2de", borderRadius: 6, fontSize: 13, marginBottom: 10 }} />

                    <div className="jg-mono" style={{ fontSize: 9.5, color: "#8a9195", marginBottom: 4 }}>TITLE</div>
                    <input type="text" value={editDraft.title} onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })}
                      style={{ width: "100%", padding: "7px 9px", border: "1px solid #dde2de", borderRadius: 6, fontSize: 13, marginBottom: 10 }} />

                    <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div className="jg-mono" style={{ fontSize: 9.5, color: "#8a9195", marginBottom: 4 }}>{cfg.tagLabel.toUpperCase()}</div>
                        <select value={editDraft.tag} onChange={(e) => setEditDraft({ ...editDraft, tag: e.target.value })}
                          style={{ width: "100%", padding: "7px 9px", border: "1px solid #dde2de", borderRadius: 6, fontSize: 13 }}>
                          {cfg.tagOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="jg-mono" style={{ fontSize: 9.5, color: "#8a9195", marginBottom: 4 }}>TYPE</div>
                        <input type="text" value={editDraft.type} onChange={(e) => setEditDraft({ ...editDraft, type: e.target.value })}
                          style={{ width: "100%", padding: "7px 9px", border: "1px solid #dde2de", borderRadius: 6, fontSize: 13 }} />
                      </div>
                    </div>

                    <div className="jg-mono" style={{ fontSize: 9.5, color: "#8a9195", marginBottom: 4 }}>{cfg.stepsLabel.toUpperCase()} (ONE PER LINE)</div>
                    <textarea rows={3} value={editDraft.stepsText} onChange={(e) => setEditDraft({ ...editDraft, stepsText: e.target.value })}
                      style={{ width: "100%", padding: "7px 9px", border: "1px solid #dde2de", borderRadius: 6, fontSize: 13, marginBottom: 10, fontFamily: "inherit" }} />

                    <div className="jg-mono" style={{ fontSize: 9.5, color: "#8a9195", marginBottom: 4 }}>{cfg.bodyLabel.toUpperCase()}</div>
                    <textarea rows={2} value={editDraft.body} onChange={(e) => setEditDraft({ ...editDraft, body: e.target.value })}
                      style={{ width: "100%", padding: "7px 9px", border: "1px solid #dde2de", borderRadius: 6, fontSize: 13, marginBottom: 12, fontFamily: "inherit" }} />

                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#3a3d40", cursor: "pointer" }}>
                        <input type="checkbox" checked={editDraft.repeat} onChange={(e) => setEditDraft({ ...editDraft, repeat: e.target.checked })} />
                        Repeat
                      </label>
                      {editDraft.repeat && (
                        <select value={editDraft.repeatFreq} onChange={(e) => setEditDraft({ ...editDraft, repeatFreq: e.target.value })}
                          style={{ padding: "6px 8px", border: "1px solid #dde2de", borderRadius: 6, fontSize: 12.5 }}>
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      )}
                    </div>
                    {editDraft.repeat && (
                      <div className="jg-mono" style={{ fontSize: 10, color: "#8a9195", marginBottom: 12 }}>
                        Will create future copies of this item, starting from its date, up to ~6 months out.
                      </div>
                    )}

                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => saveEdit(selectedKey, v.id, v)} style={{ background: "#3ea4b1", color: "#fff", border: "none", borderRadius: 6, padding: "8px 14px", fontSize: 12.5, fontWeight: 600 }}>Save</button>
                      <button onClick={cancelEdit} style={{ background: "#eef1ee", color: "#3a3d40", border: "none", borderRadius: 6, padding: "8px 14px", fontSize: 12.5 }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#14171a", margin: "6px 0 8px", textDecoration: v.done ? "line-through" : "none" }}>{v.title}</div>

                    <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
                      <span className="jg-mono" style={{ fontSize: 9.5, padding: "3px 8px", borderRadius: 5, background: swatchFor(cfg, v.tag).bg, color: swatchFor(cfg, v.tag).fg }}>{v.tag}</span>
                      <span className="jg-mono" style={{ fontSize: 9.5, padding: "3px 8px", borderRadius: 5, background: "#eef1ee", color: "#6b7280" }}>{v.type}</span>
                      {v.source === "ai-scheduled" && <span className="jg-mono" style={{ fontSize: 9.5, padding: "3px 8px", borderRadius: 5, background: "#fdedc2", color: "#8a6b0a" }}>AI-SCHEDULED</span>}
                      {v.source === "manual" && <span className="jg-mono" style={{ fontSize: 9.5, padding: "3px 8px", borderRadius: 5, background: "#e4f3f4", color: "#1f6e79" }}>MANUAL</span>}
                    </div>

                    <div style={{ marginBottom: 10 }}>
                      <div className="jg-mono" style={{ fontSize: 9.5, color: "#8a9195", marginBottom: 5 }}>{cfg.stepsLabel.toUpperCase()}</div>
                      {v.steps.map((s, si) => (
                        <div key={si} style={{ display: "flex", gap: 8, marginBottom: 6, fontSize: 12.5, color: "#3a3d40", lineHeight: 1.35 }}>
                          <span className="jg-mono" style={{ color: "#3ea4b1", flexShrink: 0 }}>{String(si + 1).padStart(2, "0")}</span>
                          <span>{s}</span>
                        </div>
                      ))}
                    </div>

                    <div style={{ marginBottom: 12 }}>
                      <div className="jg-mono" style={{ fontSize: 9.5, color: "#8a9195", marginBottom: 5 }}>{cfg.bodyLabel.toUpperCase()}</div>
                      <div style={{ fontSize: 12.5, color: "#3a3d40", background: "#eef1ee", borderRadius: 6, padding: 10, lineHeight: 1.35 }}>{v.body}</div>
                    </div>

                    <div style={{ marginBottom: canManage ? 12 : 0 }}>
                      <div className="jg-mono" style={{ fontSize: 9.5, color: "#8a9195", marginBottom: 5 }}>{cfg.linkLabel.toUpperCase()}</div>
                      <input
                        type="text"
                        defaultValue={v.link}
                        onBlur={(e) => {
                          const val = e.target.value;
                          const patch = { link: val };
                          if (val && val !== v.link) {
                            patch.linkAddedBy = profile?.full_name || "Someone";
                            patch.linkAddedAt = new Date().toISOString();
                          }
                          updateItem(selectedKey, v.id, patch);
                        }}
                        placeholder={cfg.linkPlaceholder}
                        style={{ width: "100%", padding: "8px 10px", border: "1px solid #dde2de", borderRadius: 6, fontSize: 12.5 }}
                      />
                      {v.link && <a href={v.link} target="_blank" rel="noreferrer" style={{ fontSize: 11.5, color: "#3ea4b1", marginTop: 5, display: "inline-block" }}>Open →</a>}
                      {v.link && v.linkAddedBy && (
                        <div className="jg-mono" style={{ fontSize: 9, color: "#8a9195", marginTop: 4 }}>Added by {v.linkAddedBy} · {fmtStamp(v.linkAddedAt)}</div>
                      )}
                    </div>

                    {canManage && (
                      <div style={{ display: "flex", gap: 8, borderTop: "1px solid #eef1ee", paddingTop: 12 }}>
                        <button onClick={() => startEdit(v.id, v)} style={{ background: "#eef1ee", color: "#3a3d40", border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 12 }}>Edit</button>
                        <button onClick={() => deleteItem(selectedKey, v.id)} style={{ background: "#fbe6de", color: "#a53f1f", border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 12 }}>Delete</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New content modal */}
      {showNewModal && newForm && cfg && (
        <div onClick={() => setShowNewModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(20,23,26,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 440, maxWidth: "92vw", maxHeight: "88vh", overflowY: "auto", background: "#fff", borderRadius: 12, padding: 24 }}>
            <div className="jg-mono" style={{ fontSize: 11, color: "#3ea4b1", marginBottom: 4 }}>{cfg.label.toUpperCase()}</div>
            <h2 className="jg-display" style={{ margin: "0 0 18px", fontSize: 20, color: "#14171a" }}>New {cfg.itemNoun}</h2>

            <div className="jg-mono" style={{ fontSize: 9.5, color: "#8a9195", marginBottom: 4 }}>DATE</div>
            <input type="date" value={newForm.date} onChange={(e) => setNewForm({ ...newForm, date: e.target.value })}
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #dde2de", borderRadius: 6, fontSize: 13, marginBottom: 12 }} />

            <div className="jg-mono" style={{ fontSize: 9.5, color: "#8a9195", marginBottom: 4 }}>TITLE</div>
            <input type="text" value={newForm.title} onChange={(e) => setNewForm({ ...newForm, title: e.target.value })}
              placeholder="What's the content idea?"
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #dde2de", borderRadius: 6, fontSize: 13, marginBottom: 12 }} />

            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <div className="jg-mono" style={{ fontSize: 9.5, color: "#8a9195", marginBottom: 4 }}>{cfg.tagLabel.toUpperCase()}</div>
                <select value={newForm.tag} onChange={(e) => setNewForm({ ...newForm, tag: e.target.value })}
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #dde2de", borderRadius: 6, fontSize: 13 }}>
                  {cfg.tagOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <div className="jg-mono" style={{ fontSize: 9.5, color: "#8a9195", marginBottom: 4 }}>TYPE</div>
                <input type="text" value={newForm.type} onChange={(e) => setNewForm({ ...newForm, type: e.target.value })}
                  placeholder="e.g. Product Spotlight"
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #dde2de", borderRadius: 6, fontSize: 13 }} />
              </div>
            </div>

            <div className="jg-mono" style={{ fontSize: 9.5, color: "#8a9195", marginBottom: 4 }}>{cfg.stepsLabel.toUpperCase()} (ONE PER LINE)</div>
            <textarea rows={3} value={newForm.stepsText} onChange={(e) => setNewForm({ ...newForm, stepsText: e.target.value })}
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #dde2de", borderRadius: 6, fontSize: 13, marginBottom: 12, fontFamily: "inherit" }} />

            <div className="jg-mono" style={{ fontSize: 9.5, color: "#8a9195", marginBottom: 4 }}>{cfg.bodyLabel.toUpperCase()}</div>
            <textarea rows={2} value={newForm.body} onChange={(e) => setNewForm({ ...newForm, body: e.target.value })}
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #dde2de", borderRadius: 6, fontSize: 13, marginBottom: 18, fontFamily: "inherit" }} />

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#3a3d40", cursor: "pointer" }}>
                <input type="checkbox" checked={newForm.repeat} onChange={(e) => setNewForm({ ...newForm, repeat: e.target.checked })} />
                Repeat
              </label>
              {newForm.repeat && (
                <select value={newForm.repeatFreq} onChange={(e) => setNewForm({ ...newForm, repeatFreq: e.target.value })}
                  style={{ padding: "6px 8px", border: "1px solid #dde2de", borderRadius: 6, fontSize: 12.5 }}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              )}
            </div>
            {newForm.repeat && (
              <div className="jg-mono" style={{ fontSize: 10, color: "#8a9195", marginBottom: 12 }}>
                Will also create future copies of this item, up to ~6 months out.
              </div>
            )}

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={submitNewItem} style={{ background: "#3ea4b1", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 13, fontWeight: 600 }}>Add to Calendar</button>
              <button onClick={() => setShowNewModal(false)} style={{ background: "#eef1ee", color: "#3a3d40", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 13 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* New / edit link modal */}
      {showLinkModal && linkForm && (
        <div onClick={() => setShowLinkModal(false)} style={{ position: "fixed", inset: 0, background: "rgba(20,23,26,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 420, maxWidth: "92vw", background: "#fff", borderRadius: 12, padding: 24 }}>
            <div className="jg-mono" style={{ fontSize: 11, color: "#3ea4b1", marginBottom: 4 }}>LINKS & INFORMATION</div>
            <h2 className="jg-display" style={{ margin: "0 0 18px", fontSize: 20, color: "#14171a" }}>{editingLinkId ? "Edit Link" : "New Link"}</h2>

            <div className="jg-mono" style={{ fontSize: 9.5, color: "#8a9195", marginBottom: 4 }}>TITLE</div>
            <input type="text" value={linkForm.title} onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })}
              placeholder="e.g. Printavo Dashboard"
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #dde2de", borderRadius: 6, fontSize: 13, marginBottom: 12 }} />

            <div className="jg-mono" style={{ fontSize: 9.5, color: "#8a9195", marginBottom: 4 }}>URL</div>
            <input type="text" value={linkForm.url} onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })}
              placeholder="https://…"
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #dde2de", borderRadius: 6, fontSize: 13, marginBottom: 12 }} />

            <div className="jg-mono" style={{ fontSize: 9.5, color: "#8a9195", marginBottom: 4 }}>NOTES</div>
            <textarea rows={3} value={linkForm.notes} onChange={(e) => setLinkForm({ ...linkForm, notes: e.target.value })}
              placeholder="Login reminders, deploy commands, context…"
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #dde2de", borderRadius: 6, fontSize: 13, marginBottom: 18, fontFamily: "inherit" }} />

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={submitLinkForm} style={{ background: "#3ea4b1", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 13, fontWeight: 600 }}>{editingLinkId ? "Save" : "Add Link"}</button>
              <button onClick={() => { setShowLinkModal(false); setLinkForm(null); setEditingLinkId(null); }} style={{ background: "#eef1ee", color: "#3a3d40", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 13 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
